import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Button, TextField, IconButton, Chip, Avatar,
  Card, CardContent, Grid, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, Tooltip, LinearProgress, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Select, MenuItem, FormControl, InputLabel, Badge, Divider, Collapse,
  List, ListItem, ListItemText, ListItemIcon, CircularProgress, Switch,
  Checkbox, FormControlLabel,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PhoneIcon from "@mui/icons-material/Phone";
import BarChartIcon from "@mui/icons-material/BarChart";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import QuizIcon from "@mui/icons-material/Quiz";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import EmailIcon from "@mui/icons-material/Email";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LogoutIcon from "@mui/icons-material/Logout";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import InboxIcon from "@mui/icons-material/Inbox";
import ReplyIcon from "@mui/icons-material/Reply";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import LinkIcon from "@mui/icons-material/Link";
import TimerIcon from "@mui/icons-material/Timer";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InventoryIcon from "@mui/icons-material/Inventory";
import StorageIcon from "@mui/icons-material/Storage";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaymentIcon from "@mui/icons-material/Payment";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SyncIcon from "@mui/icons-material/Sync";
import DraftsIcon from "@mui/icons-material/Drafts";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import SettingsIcon from "@mui/icons-material/Settings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import HistoryIcon from "@mui/icons-material/History";
import PaletteIcon from "@mui/icons-material/Palette";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import ImageIcon from "@mui/icons-material/Image";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { API_URL } from "./config";

/* ═══════════════════ THEME ═══════════════════ */
const C = {
  primary: "#4f46e5", primaryLight: "#eef2ff",
  dark: "#1e293b", mid: "#475569", light: "#94a3b8",
  bg: "#f8fafc", white: "#fff",
  border: "#e2e8f0",
  green: "#10b981", greenBg: "#ecfdf5",
  red: "#ef4444", redBg: "#fef2f2",
  amber: "#f59e0b", amberBg: "#fffbeb",
  blue: "#3b82f6", blueBg: "#eff6ff",
  pink: "#ec4899", pinkBg: "#fdf2f8",
  purple: "#7c3aed", purpleBg: "#f5f3ff",
};

const fadeIn = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); }`;

const StyledCard = styled(Card)({
  borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: "0 2px 12px rgba(0,0,0,.04)",
  transition: "all .25s", "&:hover": { boxShadow: "0 8px 30px rgba(0,0,0,.08)", transform: "translateY(-2px)" },
});

const StatCard = ({ icon, label, value, color, bg }) => (
  <StyledCard>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: ".82rem", color: C.light, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: C.dark }}>{value}</Typography>
      </Box>
    </CardContent>
  </StyledCard>
);

/* ═══════════════════ API HELPER ═══════════════════ */
function handleLogout() {
  localStorage.removeItem("crm_token");
  localStorage.removeItem("crm_agent");
  localStorage.removeItem("crm_role");
  window.location.reload();
}

const api = {
  token: null,
  headers() { return { "Content-Type": "application/json", "x-crm-token": this.token }; },
  async _req(r) { if (r.status === 401) { handleLogout(); throw new Error("unauthorized"); } return r.json(); },
  async get(path) { const r = await fetch(`${API_URL}/crm${path}`, { headers: this.headers() }); return this._req(r); },
  async post(path, body) { const r = await fetch(`${API_URL}/crm${path}`, { method: "POST", headers: this.headers(), body: JSON.stringify(body) }); return this._req(r); },
  async put(path, body) { const r = await fetch(`${API_URL}/crm${path}`, { method: "PUT", headers: this.headers(), body: JSON.stringify(body) }); return this._req(r); },
  async del(path) { const r = await fetch(`${API_URL}/crm${path}`, { method: "DELETE", headers: this.headers() }); return this._req(r); },
};

/* ═══════════════════ LOGIN SCREEN ═══════════════════ */
const CrmLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/crm/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password: pw }),
      });
      const data = await res.json();
      if (data.status === "success") {
        api.token = data.token;
        localStorage.setItem("crm_token", data.token);
        localStorage.setItem("crm_agent", JSON.stringify(data.agent));
        localStorage.setItem("crm_role", data.role);
        onLogin(data.agent, data.role);
      } else {
        setError(data.message || "שגיאה בהתחברות");
      }
    } catch {
      setError("שגיאת חיבור");
    }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
      <Card sx={{ width: 420, borderRadius: 4, p: 4, textAlign: "center" }}>
        <SupportAgentIcon sx={{ fontSize: 56, color: C.primary, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark, mb: 1 }}>פאנל ניהול CRM</Typography>
        <Typography sx={{ color: C.light, mb: 3 }}>קליקרים — ניהול לקוחות</Typography>
        <TextField
          fullWidth label="שם משתמש" value={username}
          onChange={e => setUsername(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth label="סיסמה" type="password" value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          sx={{ mb: 2 }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button fullWidth variant="contained" onClick={handleLogin} disabled={loading}
          sx={{ py: 1.5, borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : "כניסה"}
        </Button>
      </Card>
    </Box>
  );
};

/* ═══════════════════ SIDEBAR ═══════════════════ */
const sidebarItems = [
  { id: "dashboard", label: "דשבורד", icon: <DashboardIcon /> },
  { id: "clients", label: "לקוחות", icon: <PeopleIcon /> },
  { id: "tickets", label: "פניות", icon: <InboxIcon /> },
  { id: "phones", label: "מלאי מספרים", icon: <PhoneIcon /> },
  { id: "purchases", label: "רכישות", icon: <ShoppingCartIcon /> },
  { id: "packagesManage", label: "ניהול חבילות", icon: <InventoryIcon />, adminOnly: true },
  { id: "agents", label: "נציגים", icon: <AdminPanelSettingsIcon />, adminOnly: true },
  { id: "broadcast", label: "תפוצה", icon: <CampaignIcon />, adminOnly: true },
  { id: "manage", label: "ניהול", icon: <SettingsIcon />, adminOnly: true },
  { id: "ai", label: "AI", icon: <AutoAwesomeIcon />, adminOnly: true },
  { id: "stats", label: "סטטיסטיקות", icon: <BarChartIcon /> },
  { id: "games", label: "משחקים", icon: <SportsEsportsIcon /> },
];

const Sidebar = ({ active, onChange, ticketsBadge, currentAgent }) => {
  const role = currentAgent?.role || "agent";
  const visibleItems = sidebarItems.filter(item => !item.adminOnly || role === "admin");

  return (
    <Box sx={{
      width: 240, height: "100vh", background: C.dark, color: "#fff",
      display: "flex", flexDirection: "column", position: "fixed", left: 0, top: 0, zIndex: 100,
      overflowY: "auto",
    }}>
      <Box sx={{ p: 3, borderBottom: "1px solid rgba(255,255,255,.1)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SupportAgentIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>CRM קליקרים</Typography>
            <Typography sx={{ fontSize: ".7rem", opacity: .5 }}>פאנל ניהול</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, py: 2 }}>
        {visibleItems.map(item => (
          <Box
            key={item.id}
            onClick={() => onChange(item.id)}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 1.5,
              cursor: "pointer", transition: "all .2s", borderRadius: "0 12px 12px 0", mr: 1,
              background: active === item.id ? "rgba(79,70,229,.25)" : "transparent",
              color: active === item.id ? "#fff" : "rgba(255,255,255,.55)",
              "&:hover": { background: "rgba(255,255,255,.08)", color: "#fff" },
            }}
          >
            {item.id === "tickets" ? (
              <Badge badgeContent={ticketsBadge} color="error" max={99}>{item.icon}</Badge>
            ) : item.icon}
            <Typography sx={{ fontWeight: active === item.id ? 700 : 500, fontSize: ".9rem" }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Logged-in user info */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,.1)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, px: 1 }}>
          <Avatar sx={{ width: 32, height: 32, background: role === "admin" ? C.amber : C.primary, fontSize: ".8rem", fontWeight: 700 }}>
            {currentAgent?.name?.[0] || "?"}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: ".82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{currentAgent?.name || "—"}</Typography>
            <Typography sx={{ fontSize: ".65rem", opacity: .5 }}>{role === "admin" ? "מנהל ראשי" : "נציג"}</Typography>
          </Box>
        </Box>
        <Button fullWidth startIcon={<LogoutIcon />} onClick={handleLogout}
          sx={{ color: "rgba(255,255,255,.5)", textTransform: "none", justifyContent: "flex-start", "&:hover": { color: "#fff", background: "rgba(255,255,255,.08)" } }}>
          התנתקות
        </Button>
      </Box>
    </Box>
  );
};

/* ═══════════════════ DASHBOARD ═══════════════════ */
const ClickableStatCard = ({ icon, label, value, color, bg, onClick }) => (
  <StyledCard onClick={onClick} sx={{ cursor: onClick ? "pointer" : "default" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "14px", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: ".82rem", color: C.light, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: C.dark }}>{value}</Typography>
      </Box>
    </CardContent>
  </StyledCard>
);

const Dashboard = ({ stats, loading, onNavigate }) => {
  if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
  if (!stats) return null;
  const o = stats.overview;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark, mb: 3 }}>📊 דשבורד כללי</Typography>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("clients")} icon={<PeopleIcon />} label="סה״כ לקוחות" value={o.totalClients} color={C.primary} bg={C.primaryLight} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("games")} icon={<SportsEsportsIcon />} label="סה״כ משחקים" value={o.totalGames} color={C.green} bg={C.greenBg} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("stats")} icon={<GroupsIcon />} label="סה״כ שחקנים" value={o.totalPlayers?.toLocaleString()} color={C.blue} bg={C.blueBg} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("stats")} icon={<QuizIcon />} label="סה״כ שאלות" value={o.totalQuestions?.toLocaleString()} color={C.purple} bg={C.purpleBg} /></Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("phones")} icon={<PhoneIcon />} label="מספרים פנויים" value={`${o.freePhones} / ${o.totalPhones}`} color={C.green} bg={C.greenBg} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("phones")} icon={<PhoneIcon />} label="מספרים תפוסים" value={o.busyPhones} color={C.red} bg={C.redBg} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("tickets")} icon={<InboxIcon />} label="פניות פתוחות" value={o.openTickets} color={C.amber} bg={C.amberBg} /></Grid>
        <Grid item xs={12} sm={6} md={3}><ClickableStatCard onClick={() => onNavigate("games")} icon={<SportsEsportsIcon />} label="משחקים פעילים" value={o.activeGames} color={C.pink} bg={C.pinkBg} /></Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}><ClickableStatCard onClick={() => onNavigate("purchases")} icon={<CreditCardIcon />} label="סה״כ הכנסות" value={`₪${(o.totalRevenue || 0).toLocaleString()}`} color={C.green} bg={C.greenBg} /></Grid>
        <Grid item xs={12} sm={6} md={4}><ClickableStatCard onClick={() => onNavigate("purchases")} icon={<ReceiptLongIcon />} label="רכישות" value={o.totalPurchases || 0} color={C.primary} bg={C.primaryLight} /></Grid>
        <Grid item xs={12} sm={6} md={4}><ClickableStatCard onClick={() => onNavigate("purchases")} icon={<PeopleIcon />} label="לקוחות משלמים / ∞" value={`${o.payingClients || 0} / ${o.unlimitedClients || 0}`} color={C.blue} bg={C.blueBg} /></Grid>
      </Grid>

      {/* הכנסות לפי תקופה */}
      {stats.revenue && (
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StyledCard>
              <CardContent>
                <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>💰 הכנסות</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                  {[
                    { label: "היום", val: `₪${stats.revenue.today?.toLocaleString() || 0}`, sub: `${stats.revenue.purchasesToday || 0} עסקאות`, color: C.green },
                    { label: "השבוע", val: `₪${stats.revenue.week?.toLocaleString() || 0}`, sub: `${stats.revenue.purchasesWeek || 0} עסקאות`, color: C.blue },
                    { label: "החודש", val: `₪${stats.revenue.month?.toLocaleString() || 0}`, sub: `${stats.revenue.purchasesMonth || 0} עסקאות`, color: C.purple },
                  ].map((x, i) => (
                    <Box key={i} sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, color: x.color }}>{x.val}</Typography>
                      <Typography sx={{ fontSize: ".68rem", color: C.light }}>{x.sub}</Typography>
                      <Typography sx={{ fontSize: ".78rem", color: C.light }}>{x.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={8}>
            <StyledCard>
              <CardContent>
                <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>📈 הכנסות — 30 יום אחרונים</Typography>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 100 }}>
                  {stats.revenueChart?.map((d, i) => {
                    const maxVal = Math.max(...stats.revenueChart.map(x => x.revenue), 1);
                    const h = (d.revenue / maxVal) * 80 + 4;
                    return (
                      <Tooltip key={i} title={`${d.date}: ₪${d.revenue} (${d.count} עסקאות)`} arrow>
                        <Box sx={{
                          flex: 1, height: h, borderRadius: "4px 4px 0 0",
                          background: d.revenue > 0 ? `linear-gradient(180deg, ${C.green}, #34d399)` : C.border,
                          transition: "height .3s", cursor: "pointer",
                          "&:hover": { opacity: .8 },
                        }} />
                      </Tooltip>
                    );
                  })}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}

      {/* לקוחות חדשים */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>👤 לקוחות חדשים</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                {[
                  { label: "היום", val: stats.newClients?.today, color: C.green },
                  { label: "השבוע", val: stats.newClients?.week, color: C.blue },
                  { label: "החודש", val: stats.newClients?.month, color: C.purple },
                ].map((x, i) => (
                  <Box key={i} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, color: x.color }}>{x.val}</Typography>
                    <Typography sx={{ fontSize: ".78rem", color: C.light }}>{x.label}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>📈 הרשמות — 30 יום אחרונים</Typography>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 100 }}>
                {stats.registrationChart?.map((d, i) => {
                  const maxVal = Math.max(...stats.registrationChart.map(x => x.count), 1);
                  const h = (d.count / maxVal) * 80 + 4;
                  return (
                    <Tooltip key={i} title={`${d.date}: ${d.count} הרשמות`} arrow>
                      <Box sx={{
                        flex: 1, height: h, borderRadius: "4px 4px 0 0",
                        background: d.count > 0 ? `linear-gradient(180deg, ${C.primary}, #818cf8)` : C.border,
                        transition: "height .3s", cursor: "pointer",
                        "&:hover": { opacity: .8 },
                      }} />
                    </Tooltip>
                  );
                })}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* טופ לקוחות + משחקים */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>🏆 לקוחות מובילים</Typography>
              {stats.topClients?.map((c, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: i < stats.topClients.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, background: [C.primary, C.green, C.amber, C.pink, C.blue][i % 5], fontSize: ".8rem", fontWeight: 700 }}>{i + 1}</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: ".88rem", color: C.dark }}>{c.name}</Typography>
                      <Typography sx={{ fontSize: ".72rem", color: C.light }}>{c.phone}</Typography>
                    </Box>
                  </Box>
                  <Chip label={`${c.gamesCount} משחקים`} size="small" sx={{ fontWeight: 600 }} />
                </Box>
              ))}
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>🎮 משחקים פופולריים</Typography>
              {stats.topGames?.map((g, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, borderBottom: i < stats.topGames.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: ".88rem", color: C.dark }}>{g.name}</Typography>
                    <Typography sx={{ fontSize: ".72rem", color: C.light }}>{g.owner}</Typography>
                  </Box>
                  <Chip label={`${g.playersCount} שחקנים`} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
                </Box>
              ))}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
  );
};

/* ═══════════════════ CLIENTS PAGE ═══════════════════ */
const ClientsPage = ({ clients, loading, onRefresh, snack, onViewClient }) => {
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [pwDialog, setPwDialog] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  const [grantDialog, setGrantDialog] = useState(null);
  const [grantPackages, setGrantPackages] = useState([]);
  const [grantSelectedPkg, setGrantSelectedPkg] = useState("");

  const filtered = clients.filter(c =>
    `${c.first_name} ${c.last_name} ${c.phone} ${c.email} ${c.user_id}`
      .toLowerCase().includes(search.toLowerCase())
  );

  const handleSaveEdit = async () => {
    if (!editDialog) return;
    const res = await api.put(`/clients/${editDialog.user_id}`, editDialog);
    if (res.status === "success") {
      snack("לקוח עודכן בהצלחה", "success");
      setEditDialog(null);
      onRefresh();
    } else {
      snack("שגיאה בעדכון", "error");
    }
  };

  const handleResetPw = async () => {
    if (!pwDialog || !newPw) return;
    const res = await api.post(`/clients/${pwDialog.user_id}/reset-password`, { newPassword: newPw });
    if (res.status === "success") {
      snack("סיסמה שונתה בהצלחה", "success");
      setPwDialog(null);
      setNewPw("");
    } else {
      snack(res.error || "שגיאה", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const res = await api.del(`/clients/${deleteDialog.user_id}`);
    if (res.status === "success") {
      snack("לקוח נמחק", "success");
      setDeleteDialog(null);
      onRefresh();
    }
  };

  const handleGrantPackage = async () => {
    if (!grantDialog || !grantSelectedPkg) return;
    const res = await api.post("/grant-package", { user_id: grantDialog.user_id, packageId: grantSelectedPkg });
    if (res.status === "success") {
      snack("חבילה הוענקה בהצלחה", "success");
      setGrantDialog(null);
      setGrantSelectedPkg("");
      onRefresh();
    } else {
      snack(res.message || "שגיאה", "error");
    }
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>👥 ניהול לקוחות ({filtered.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField size="small" placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
            sx={{ width: 250, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {filtered.map((client) => (
          <Grid item xs={12} md={6} lg={4} key={client.user_id}>
            <StyledCard>
              <CardContent sx={{ p: 2.5 }}>
                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ width: 44, height: 44, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`, fontWeight: 700, fontSize: ".95rem" }}>
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: C.dark, fontSize: ".95rem" }}>
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography sx={{ fontSize: ".72rem", color: C.light }}>
                        {client.createdAt ? `הצטרף ${client.createdAt}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setExpandedClient(expandedClient === client.user_id ? null : client.user_id)}>
                    {expandedClient === client.user_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Info */}
                <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                  <Chip icon={<PhoneAndroidIcon sx={{ fontSize: 14 }} />} label={client.phone || "—"} size="small" variant="outlined" />
                  <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={client.email || "—"} size="small" variant="outlined" />
                </Box>

                {/* Stats */}
                <Box sx={{ display: "flex", gap: 1.5, mb: 2, justifyContent: "space-around" }}>
                  {[
                    { label: "משחקים", val: client.gamesCount || 0, color: C.primary },
                    { label: "פעילים", val: client.activeGames || 0, color: C.green },
                    { label: "שחקנים", val: client.totalPlayers || 0, color: C.blue },
                    { label: "שאלות", val: client.totalQuestions || 0, color: C.purple },
                  ].map((s, i) => (
                    <Box key={i} sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: s.color }}>{s.val}</Typography>
                      <Typography sx={{ fontSize: ".65rem", color: C.light }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Actions */}
                <Box sx={{ display: "flex", gap: .8, justifyContent: "center" }}>
                  <Tooltip title="ערוך">
                    <IconButton size="small" onClick={() => setEditDialog({ ...client })} sx={{ color: C.primary }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="שנה סיסמה">
                    <IconButton size="small" onClick={() => setPwDialog(client)} sx={{ color: C.amber }}>
                      <LockResetIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="העתק ID">
                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(client.user_id); snack("ID הועתק", "info"); }} sx={{ color: C.light }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="מחק">
                    <IconButton size="small" onClick={() => setDeleteDialog(client)} sx={{ color: C.red }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="הענק חבילה">
                    <IconButton size="small" onClick={async () => {
                      setGrantDialog(client);
                      setGrantSelectedPkg("");
                      try { const pkgs = await api.get("/packages"); setGrantPackages(pkgs || []); } catch { setGrantPackages([]); }
                    }} sx={{ color: C.green }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="צפה בפרטים">
                    <IconButton size="small" onClick={() => onViewClient && onViewClient(client.user_id)} sx={{ color: C.blue }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Expanded — games list */}
                <Collapse in={expandedClient === client.user_id}>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: C.dark, mb: 1 }}>משחקים:</Typography>
                  {client.games?.length > 0 ? client.games.map((g, i) => (
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: .5, fontSize: ".8rem" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: .8 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 8, color: g.start ? C.green : C.light }} />
                        <Typography sx={{ fontSize: ".82rem", color: C.dark }}>{g.name}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: ".72rem", color: C.light }}>{g.start || "ללא מספר"}</Typography>
                    </Box>
                  )) : <Typography sx={{ fontSize: ".78rem", color: C.light }}>אין משחקים</Typography>}

                  {/* חבילה ומכסה */}
                  {client.quota && (
                    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: C.bg }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: .5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: C.dark }}>📦 חבילה:</Typography>
                        {client.currentPackageName && (
                          <Chip label={client.currentPackageName} size="small" sx={{ fontWeight: 700, background: C.primaryLight, color: C.primary, fontSize: ".72rem" }} />
                        )}
                      </Box>
                      {!client.currentPackageName && client.quota.gamesAllowed === 0 && (
                        <Typography sx={{ fontSize: ".76rem", color: C.red, mb: .5 }}>⚠️ אין חבילה פעילה</Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 2, fontSize: ".78rem" }}>
                        <Typography sx={{ fontSize: ".78rem", color: C.mid }}>
                          הפעלות: <strong style={{ color: C.primary }}>
                            {client.quota.gamesAllowed === -1 ? "♾️ ללא הגבלה" : `${client.gamesCount || 0} / ${client.quota.gamesAllowed || 0}`}
                          </strong>
                        </Typography>
                        <Typography sx={{ fontSize: ".78rem", color: C.mid }}>
                          שחקנים: <strong style={{ color: C.blue }}>{client.quota.maxPlayersPerGame || 0}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: ".78rem", color: C.mid }}>
                          אחסון: <strong style={{ color: C.primary }}>{Math.round((client.storageUsedBytes || 0) / 1024 / 1024)} MB</strong> / {client.quota.storageMB || 500} MB
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Typography sx={{ fontSize: ".68rem", color: C.light, mt: 1 }}>ID: {client.user_id}</Typography>
                </Collapse>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>✏️ עריכת לקוח</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editDialog && (
            <Grid container spacing={2} sx={{ mt: .5 }}>
              <Grid item xs={6}><TextField fullWidth label="שם פרטי" value={editDialog.first_name || ""} onChange={e => setEditDialog({ ...editDialog, first_name: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="שם משפחה" value={editDialog.last_name || ""} onChange={e => setEditDialog({ ...editDialog, last_name: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="טלפון" value={editDialog.phone || ""} onChange={e => setEditDialog({ ...editDialog, phone: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="אימייל" value={editDialog.email || ""} onChange={e => setEditDialog({ ...editDialog, email: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="הערות" multiline rows={2} value={editDialog.notes || ""} onChange={e => setEditDialog({ ...editDialog, notes: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(null)}>ביטול</Button>
          <Button variant="contained" onClick={handleSaveEdit} sx={{ borderRadius: 3, fontWeight: 700 }}>שמור</Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={!!pwDialog} onClose={() => { setPwDialog(null); setNewPw(""); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🔑 שינוי סיסמה — {pwDialog?.first_name} {pwDialog?.last_name}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="סיסמה חדשה" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setPwDialog(null); setNewPw(""); }}>ביטול</Button>
          <Button variant="contained" color="warning" onClick={handleResetPw} sx={{ borderRadius: 3, fontWeight: 700 }}>שנה סיסמה</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle sx={{ fontWeight: 700, color: C.red }}>⚠️ מחיקת לקוח</DialogTitle>
        <DialogContent>
          <Typography>האם למחוק את <strong>{deleteDialog?.first_name} {deleteDialog?.last_name}</strong> וכל הנתונים שלו?</Typography>
          <Typography sx={{ color: C.red, fontSize: ".85rem", mt: 1 }}>פעולה זו בלתי הפיכה!</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog(null)}>ביטול</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{ borderRadius: 3, fontWeight: 700 }}>מחק לצמיתות</Button>
        </DialogActions>
      </Dialog>

      {/* Grant Package Dialog */}
      <Dialog open={!!grantDialog} onClose={() => setGrantDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🎁 הענקת חבילה — {grantDialog?.first_name} {grantDialog?.last_name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: ".85rem", color: C.mid, mb: 2, mt: 1 }}>בחר חבילה להענקה:</Typography>
          <Box>
            {grantPackages.map(pkg => (
              <Box key={pkg.id} onClick={() => setGrantSelectedPkg(pkg.id)} sx={{
                p: 2, mb: 1.5, borderRadius: 3,
                border: `2px solid ${grantSelectedPkg === pkg.id ? C.primary : C.border}`,
                cursor: "pointer", transition: "all .2s",
                background: grantSelectedPkg === pkg.id ? C.primaryLight : "transparent",
                "&:hover": { borderColor: C.primary + "66" },
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: ".95rem", color: C.dark }}>
                      {pkg.unlimited ? "♾️ " : ""}{pkg.name}
                    </Typography>
                    <Typography sx={{ fontSize: ".78rem", color: C.light, mt: .3 }}>
                      {pkg.unlimited ? "ללא הגבלה" : `${pkg.games} הפעלות`} • עד {pkg.maxPlayers} שחקנים
                      {pkg.storageMB > 0 && ` • +${pkg.storageMB} MB`}
                    </Typography>
                  </Box>
                  {grantSelectedPkg === pkg.id && <CheckCircleIcon sx={{ color: C.primary }} />}
                </Box>
              </Box>
            ))}
            {grantPackages.length === 0 && (
              <Typography sx={{ textAlign: "center", color: C.light, py: 3 }}>אין חבילות מוגדרות</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGrantDialog(null)}>ביטול</Button>
          <Button variant="contained" onClick={handleGrantPackage} disabled={!grantSelectedPkg}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>הענק חבילה</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ EMAIL PAGE ═══════════════════ */
/* ═══════════════════ EMAIL THREADING ═══════════════════ */
// מסיר קידומות תגובה/העברה חוזרות (Re:/Fwd:/השב:/העבר:) לצורך זיהוי שיחה
const RE_SUBJECT_PREFIX = /^\s*(re|fwd|fw|השב|העבר|תגובה)\s*:\s*/i;
const normalizeSubject = (s) => {
  let out = (s || '').trim();
  let prev;
  do { prev = out; out = out.replace(RE_SUBJECT_PREFIX, '').trim(); } while (out !== prev);
  return out.toLowerCase();
};
// הצד השני בשיחה (הלקוח): בהודעה נכנסת = השולח, ביוצאת = הנמען
const threadParty = (m) => (m._dir === 'out'
  ? (m.to?.[0]?.address || '')
  : (m.from?.address || '')).toLowerCase();

// מקבץ הודעות לשיחות לפי נושא מנורמל + כתובת הצד השני
const buildThreads = (emails) => {
  const map = new Map();
  for (const m of emails) {
    const key = `${normalizeSubject(m.subject)}||${threadParty(m)}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  const threads = [];
  for (const [key, msgs] of map) {
    msgs.sort((a, b) => new Date(a.date) - new Date(b.date)); // ישן→חדש בתוך השיחה
    const last = msgs[msgs.length - 1];
    threads.push({
      key,
      messages: msgs,
      last,
      count: msgs.length,
      subject: last.subject || '(ללא נושא)',
      party: threadParty(last),
      unread: msgs.some(x => x._dir !== 'out' && !(x.flags || []).includes('\\Seen')),
    });
  }
  return threads;
};

const EmailPage = ({ snack, onSyncTickets }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedThreadKey, setSelectedThreadKey] = useState(null);
  const [sentPath, setSentPath] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: '', cc: '', bcc: '', subject: '', text: '' });
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [sending, setSending] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [filterUnread, setFilterUnread] = useState(false);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState('INBOX');
  const [folders, setFolders] = useState([]);
  const [sortBy, setSortBy] = useState('date');

  const fetchMails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/emails?folder=${encodeURIComponent(folder)}&limit=100${filterUnread ? '&unseen=true' : ''}`);
      const dir = folder === sentPath ? 'out' : 'in';
      let msgs = (Array.isArray(data) ? data : []).map(m => ({ ...m, _dir: dir, _folder: folder }));
      // בתצוגת INBOX ממזגים גם את תיקיית "נשלח" — כדי לראות שיחה מלאה (נכנס+יוצא)
      if (folder === 'INBOX' && sentPath) {
        try {
          const sent = await api.get(`/emails?folder=${encodeURIComponent(sentPath)}&limit=100`);
          const sentMsgs = (Array.isArray(sent) ? sent : []).map(m => ({ ...m, _dir: 'out', _folder: sentPath }));
          msgs = msgs.concat(sentMsgs);
        } catch { /* אם אין גישה לתיקיית הנשלחים — ממשיכים עם INBOX בלבד */ }
      }
      setEmails(msgs);
    } catch (err) {
      snack('שגיאה בטעינת מיילים', 'error');
    }
    setLoading(false);
  }, [filterUnread, folder, sentPath, snack]);

  useEffect(() => { fetchMails(); }, [fetchMails]);

  useEffect(() => {
    api.get('/emails/folders')
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setFolders(list);
        // זיהוי תיקיית הנשלחים (לפי special-use של IMAP, ואם אין — לפי שם/נתיב)
        const sent = list.find(f => f.specialUse === '\\Sent')
          || list.find(f => /sent|נשלח/i.test(f.name || '') || /sent/i.test(f.path || ''));
        if (sent) setSentPath(sent.path);
      })
      .catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/emails/sync');
      snack(`סנכרון הושלם: ${result.newTickets || 0} פניות חדשות, ${result.replies || 0} תגובות`, 'success');
      fetchMails();
      if (onSyncTickets) onSyncTickets();
    } catch {
      snack('שגיאה בסנכרון', 'error');
    }
    setSyncing(false);
  };

  const handleSend = async () => {
    if (!compose.to || !compose.subject) return snack('נא למלא נמען ונושא', 'warning');
    setSending(true);
    try {
      const payload = {
        to: compose.to,
        cc: compose.cc || undefined,
        bcc: compose.bcc || undefined,
        subject: compose.subject,
        html: `<div dir="rtl" style="font-family:Arial,sans-serif;line-height:1.8;color:#333;">${(compose.text || '').replace(/\n/g, '<br/>')}</div>`,
        text: compose.text,
      };
      const result = await api.post('/emails/send', payload);
      if (result.status === 'success') {
        snack('מייל נשלח בהצלחה!', 'success');
        setComposeOpen(false);
        setCompose({ to: '', cc: '', bcc: '', subject: '', text: '' });
        setShowCcBcc(false);
      } else {
        snack('שגיאה: ' + (result.error || ''), 'error');
      }
    } catch {
      snack('שגיאה בשליחה', 'error');
    }
    setSending(false);
  };

  const handleDeleteEmail = async (uid) => {
    try {
      await api.del(`/emails/${uid}`);
      setEmails(prev => prev.filter(e => e.uid !== uid));
      snack('מייל נמחק', 'success');
    } catch {
      snack('שגיאה במחיקה', 'error');
    }
  };

  const isUnread = (email) => email._dir !== 'out' && !(email.flags || []).includes('\\Seen');

  const unreadCount = emails.filter(isUnread).length;

  const handleMarkAllRead = async () => {
    const unread = emails.filter(isUnread);
    await Promise.all(unread.map(e => api.post(`/emails/${e.uid}/read`).catch(() => {})));
    setEmails(prev => prev.map(e => ({ ...e, flags: [...new Set([...(e.flags || []), '\\Seen'])] })));
    snack('כל המיילים סומנו כנקראו', 'success');
  };

  // סימון כל ההודעות הנכנסות בשרשור כנקראו (בעת פתיחת שיחה)
  const markThreadRead = (thread) => {
    const unread = thread.messages.filter(isUnread);
    if (unread.length === 0) return;
    unread.forEach(e => api.post(`/emails/${e.uid}/read`).catch(() => {}));
    const uids = new Set(unread.map(e => e.uid));
    setEmails(prev => prev.map(e => uids.has(e.uid) ? { ...e, flags: [...new Set([...(e.flags || []), '\\Seen'])] } : e));
  };

  // קיבוץ ההודעות לשיחות (שרשור), סינון לפי חיפוש ומיון לפי ההודעה האחרונה
  const threads = buildThreads(emails)
    .filter(t => {
      const q = search.toLowerCase();
      if (!q) return true;
      return t.messages.some(m => `${m.from?.address || ''} ${m.from?.name || ''} ${m.subject || ''} ${m.text || ''}`.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.last.date) - new Date(a.last.date);
      if (sortBy === 'date_asc') return new Date(a.last.date) - new Date(b.last.date);
      if (sortBy === 'sender') return (a.party || '').localeCompare(b.party || '', 'he');
      return 0;
    });

  const selectedThread = threads.find(t => t.key === selectedThreadKey) || null;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
  };

  if (loading && emails.length === 0) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>📧 תיבת דואר</Typography>
          {unreadCount > 0 && <Chip label={`${unreadCount} לא נקראו`} size="small" sx={{ fontWeight: 700, bgcolor: C.redBg, color: C.red }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
            sx={{ width: 190, '& .MuiOutlinedInput-root': { borderRadius: 3 } }} />
          {folders.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select value={folder} onChange={e => setFolder(e.target.value)} sx={{ borderRadius: 3 }}>
                {folders.map(f => <MenuItem key={f.path} value={f.path}>{f.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ borderRadius: 3 }}>
              <MenuItem value="date">חדש ראשון</MenuItem>
              <MenuItem value="date_asc">ישן ראשון</MenuItem>
              <MenuItem value="sender">לפי שולח</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="הצג רק לא נקראו">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
              <Switch size="small" checked={filterUnread} onChange={e => setFilterUnread(e.target.checked)} />
              <DraftsIcon sx={{ fontSize: 18, color: filterUnread ? C.primary : C.light }} />
            </Box>
          </Tooltip>
          {unreadCount > 0 && (
            <Button variant="outlined" startIcon={<MarkEmailReadIcon />} onClick={handleMarkAllRead}
              sx={{ borderRadius: 3, fontWeight: 700, fontSize: '.8rem' }}>
              סמן הכל נקרא
            </Button>
          )}
          <Button variant="outlined" startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSync} disabled={syncing}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            סנכרון לפניות
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setComposeOpen(true)}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            מייל חדש
          </Button>
          <IconButton onClick={fetchMails}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}><StatCard icon={<EmailIcon />} label="סהכ" value={emails.length} color={C.primary} bg={C.primaryLight} /></Grid>
        <Grid item xs={4}><StatCard icon={<DraftsIcon />} label="לא נקראו" value={unreadCount} color={C.amber} bg={C.amberBg} /></Grid>
        <Grid item xs={4}><StatCard icon={<MarkEmailReadIcon />} label="נקראו" value={emails.length - unreadCount} color={C.green} bg={C.greenBg} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Threads list */}
        <Grid item xs={12} md={selectedThread ? 5 : 12}>
          {threads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography sx={{ fontSize: '3rem', mb: 1 }}>📭</Typography>
              <Typography sx={{ color: C.light, fontWeight: 600 }}>אין מיילים</Typography>
            </Box>
          )}
          {threads.map(t => {
            const partyName = (t.messages.find(m => m._dir !== 'out')?.from?.name) || t.party || 'לא ידוע';
            return (
              <Box key={t.key} onClick={() => { setSelectedThreadKey(t.key); markThreadRead(t); }}
                sx={{
                  p: 2, mb: 1, borderRadius: 3, background: C.white,
                  border: `1px solid ${selectedThreadKey === t.key ? C.primary : C.border}`,
                  cursor: 'pointer', transition: 'all .2s',
                  boxShadow: selectedThreadKey === t.key ? `0 0 0 2px ${C.primary}33` : '0 1px 4px rgba(0,0,0,.03)',
                  borderRight: t.unread ? `4px solid ${C.primary}` : `4px solid transparent`,
                  '&:hover': { borderColor: C.primary + '66' },
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: .5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: .3 }}>
                      {t.unread && <FiberManualRecordIcon sx={{ fontSize: 10, color: C.primary }} />}
                      <Typography sx={{ fontWeight: t.unread ? 800 : 500, fontSize: '.88rem', color: C.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {partyName}
                      </Typography>
                      {t.count > 1 && (
                        <Chip label={t.count} size="small" sx={{ height: 18, fontSize: '.62rem', fontWeight: 700, bgcolor: C.primaryLight, color: C.primary }} />
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: t.unread ? 700 : 500, fontSize: '.82rem', color: C.mid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.subject}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, ml: 1 }}>
                    <Typography sx={{ fontSize: '.7rem', color: C.light, whiteSpace: 'nowrap' }}>{formatDate(t.last.date)}</Typography>
                    {t.messages.some(m => m.attachments?.length > 0) && <AttachFileIcon sx={{ fontSize: 14, color: C.light, mt: .3 }} />}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '.78rem', color: C.light, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.last._dir === 'out' ? '↩ ' : ''}{(t.last.text || '').slice(0, 120)}
                </Typography>
              </Box>
            );
          })}
        </Grid>

        {/* Thread detail (conversation) */}
        {selectedThread && (
          <Grid item xs={12} md={7}>
            <StyledCard sx={{ position: 'sticky', top: 20 }}>
              <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.dark, mb: .5 }}>{selectedThread.subject}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip icon={<PersonIcon sx={{ fontSize: 14 }} />} label={selectedThread.party} size="small" sx={{ fontSize: '.72rem', fontWeight: 600 }} />
                      <Typography sx={{ fontSize: '.72rem', color: C.light }}>{selectedThread.count} הודעות בשיחה</Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setSelectedThreadKey(null)}><CloseIcon fontSize="small" /></IconButton>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Conversation thread — כל ההודעות מהישנה לחדשה */}
                <Box sx={{ maxHeight: 520, overflowY: 'auto', pr: .5 }}>
                  {selectedThread.messages.map((m, i) => {
                    const isOut = m._dir === 'out';
                    return (
                      <Box key={m.uid ?? i} sx={{ display: 'flex', justifyContent: isOut ? 'flex-start' : 'flex-end', mb: 1.5 }}>
                        <Box sx={{
                          maxWidth: '92%', p: 1.75, borderRadius: 3,
                          background: isOut ? C.primaryLight : C.bg,
                          border: `1px solid ${isOut ? C.primary + '33' : C.border}`,
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: .75, flexWrap: 'wrap' }}>
                            <Chip size="small" label={isOut ? 'אנחנו' : (m.from?.name || m.from?.address || 'לקוח')}
                              sx={{ height: 20, fontSize: '.62rem', fontWeight: 700, bgcolor: isOut ? C.primary : C.mid, color: '#fff' }} />
                            <Typography sx={{ fontSize: '.68rem', color: C.light }}>
                              {m.date ? new Date(m.date).toLocaleString('he-IL') : ''}
                            </Typography>
                            {!isOut && (
                              <Tooltip title="מחק מייל">
                                <IconButton size="small" onClick={() => handleDeleteEmail(m.uid)} sx={{ color: C.red, p: .25 }}>
                                  <DeleteIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {m.attachments?.length > 0 && (
                            <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mb: 1 }}>
                              {m.attachments.map((a, ai) => (
                                <Chip key={ai} icon={<AttachFileIcon sx={{ fontSize: 13 }} />}
                                  label={`${a.filename} (${(a.size / 1024).toFixed(1)}KB)`}
                                  size="small" variant="outlined" sx={{ fontSize: '.66rem' }} />
                              ))}
                            </Box>
                          )}
                          {m.html ? (
                            <Box sx={{ fontSize: '.88rem', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: m.html }} />
                          ) : (
                            <Typography sx={{ fontSize: '.88rem', color: C.dark, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{m.text}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Reply */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button variant="contained" startIcon={<ReplyIcon />}
                    onClick={() => {
                      const lastIn = [...selectedThread.messages].reverse().find(m => m._dir !== 'out') || selectedThread.last;
                      setCompose({
                        to: selectedThread.party || lastIn.from?.address || '',
                        cc: '',
                        bcc: '',
                        subject: /^re:/i.test(selectedThread.subject) ? selectedThread.subject : `Re: ${selectedThread.subject || ''}`,
                        text: '',
                      });
                      setShowCcBcc(false);
                      setComposeOpen(true);
                    }}
                    sx={{ borderRadius: 3, fontWeight: 700, fontSize: '.8rem', background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
                    השב ללקוח
                  </Button>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        )}
      </Grid>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
          color: '#fff', px: 3, py: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,255,255,.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MailOutlineIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>שליחת מייל</Typography>
              <Typography sx={{ fontSize: '.72rem', opacity: .85 }}>cs@clickerim.co.il</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setComposeOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: .5 }}>
              <Typography sx={{ fontSize: '.8rem', fontWeight: 600, color: C.mid, minWidth: 60 }}>אל</Typography>
              <TextField fullWidth variant="standard" value={compose.to}
                onChange={e => setCompose({ ...compose, to: e.target.value })}
                placeholder="example@email.com" InputProps={{ disableUnderline: false, sx: { fontSize: '.9rem' } }} />
            </Box>
            <Collapse in={showCcBcc}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: .5 }}>
                <Typography sx={{ fontSize: '.8rem', fontWeight: 600, color: C.mid, minWidth: 60 }}>עותק</Typography>
                <TextField fullWidth variant="standard" value={compose.cc}
                  onChange={e => setCompose({ ...compose, cc: e.target.value })}
                  placeholder="cc@example.com" InputProps={{ sx: { fontSize: '.9rem' } }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: .5 }}>
                <Typography sx={{ fontSize: '.8rem', fontWeight: 600, color: C.mid, minWidth: 60 }}>עותק מוסתר</Typography>
                <TextField fullWidth variant="standard" value={compose.bcc}
                  onChange={e => setCompose({ ...compose, bcc: e.target.value })}
                  placeholder="bcc@example.com" InputProps={{ sx: { fontSize: '.9rem' } }} />
              </Box>
            </Collapse>
            {!showCcBcc && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setShowCcBcc(true)}
                  sx={{ fontSize: '.72rem', textTransform: 'none', color: C.primary, minWidth: 0 }}>
                  + הוסף עותק / עותק מוסתר
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: .5, mt: .5 }}>
              <Typography sx={{ fontSize: '.8rem', fontWeight: 600, color: C.mid, minWidth: 60 }}>נושא</Typography>
              <TextField fullWidth variant="standard" value={compose.subject}
                onChange={e => setCompose({ ...compose, subject: e.target.value })}
                placeholder="נושא המייל" InputProps={{ sx: { fontSize: '.9rem' } }} />
            </Box>
          </Box>
          <TextField
            fullWidth multiline rows={14}
            placeholder="כתוב את תוכן המייל כאן..."
            value={compose.text}
            onChange={e => setCompose(prev => ({ ...prev, text: e.target.value }))}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0, borderLeft: 'none', borderRight: 'none', borderTop: `1px solid ${C.border}` },
                  '& fieldset': { border: 'none' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${C.border}`, background: C.bg }}>
          <Button onClick={() => setComposeOpen(false)} sx={{ borderRadius: 3, color: C.mid, fontWeight: 600 }}>
            ביטול
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" onClick={handleSend} disabled={sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{
              borderRadius: 3, fontWeight: 700, px: 3,
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.purple} 100%)`,
              boxShadow: '0 4px 14px rgba(79,70,229,.35)',
              '&:hover': { boxShadow: '0 6px 20px rgba(79,70,229,.45)' },
            }}>
            {sending ? 'שולח...' : 'שלח'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ TICKETS PAGE ═══════════════════ */
/* ═══════════════════ TICKETS ═══════════════════ */
// ראשי תיבות לאווטאר מתוך שם / כתובת מייל
const getInitials = (nameOrEmail) => {
  const s = (nameOrEmail || '').trim();
  if (!s) return '?';
  const parts = s.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};
// צבע אווטאר יציב לפי מחרוזת
const avatarColor = (s) => {
  const palette = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9', '#ef4444', '#14b8a6'];
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

// מפריד הודעה חדשה מהציטוט של ההודעה הקודמת (Gmail/Outlook style).
// מזהה "בתאריך ... כתב:" / "On ... wrote:" / שורות שמתחילות ב-">" / קו מפריד.
const QUOTE_MARKERS = [
  /^\s*On\s.+\bwrote:/i,
  /^\s*בתאריך.*(מאת|כתב|כתבה|wrote)/,   // "בתאריך ... מאת ... כתב:" (Gmail עברית)
  /^\s*ב-?\s*\d{1,2}[./]\d{1,2}.+(כתב|wrote)/i,
  /^\s*-{2,}\s*(הודעה מקורית|Original Message|Forwarded message)/i,
  /^\s*_{5,}\s*$/,
  /^\s*(From|מאת):\s.+/i,
];
// ניקוי תווי כיוון נסתרים (bidi) ש-Gmail מכניס סביב כתובות מייל
const stripBidi = (s) => (s || '').replace(/[‎‏‪-‮⁦-⁩]/g, '');
const splitQuote = (raw) => {
  const text = stripBidi((raw || '').replace(/\r\n/g, '\n'));
  const lines = text.split('\n');
  let cut = -1;
  for (let i = 0; i < lines.length; i++) {
    if (QUOTE_MARKERS.some(p => p.test(lines[i])) || /^\s*>/.test(lines[i])) { cut = i; break; }
  }
  if (cut === -1) return { main: text.trim(), quoted: '' };
  const main = lines.slice(0, cut).join('\n').trim();
  // ניקוי סימני ">" מתחילת שורות הציטוט לתצוגה נקייה
  const quoted = lines.slice(cut).join('\n').replace(/^\s*>+\s?/gm, '').trim();
  return { main, quoted };
};

const TicketsPage = ({ tickets, loading, onRefresh, snack, agents, clients }) => {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [senders, setSenders] = useState([]);
  const [fromAddress, setFromAddress] = useState("");
  const editorRef = useRef(null);
  const [newTicketDialog, setNewTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sortBy, setSortBy] = useState("date_desc");
  const [localTickets, setLocalTickets] = useState(tickets);
  const [page, setPage] = useState(0);
  const [expandedQuotes, setExpandedQuotes] = useState({}); // קיפול/פתיחת ציטוטים לפי מזהה הודעה
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [agentQuery, setAgentQuery] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const threadRef = useRef(null);
  const PAGE_SIZE = 50;

  // טעינת כתובות השליחה האפשריות (מאת)
  useEffect(() => {
    api.get('/emails/senders').then(d => { const arr = Array.isArray(d) ? d : []; setSenders(arr); if (arr[0]) setFromAddress(arr[0]); }).catch(() => {});
  }, []);

  // סנכרון מרשימת ה-props; איפוס עמוד כשמסננים
  useEffect(() => { setLocalTickets(tickets); }, [tickets]);
  useEffect(() => { setPage(0); }, [filterStatus, search, sortBy]);

  // שיוך אוטומטי ללקוח קיים לפי כתובת המייל (כשפותחים פנייה שעדיין לא מקושרת)
  useEffect(() => {
    if (!selectedTicket || selectedTicket.linkedClient || !selectedTicket.email || !clients?.length) return;
    const em = selectedTicket.email.toLowerCase();
    const match = clients.find(c => (c.email || "").toLowerCase() === em);
    if (match) handleLinkClient(selectedTicket.id, match.user_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id]);

  // גלילה לתחתית השרשור (ההודעה החדשה ביותר) כשנפתחת פנייה או נוספת תגובה
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [selectedTicket?.id, selectedTicket?.replies?.length]);

  const statusColors = { open: C.amber, "in-progress": C.blue, closed: C.green };
  const statusLabels = { open: "חדשה", "in-progress": "בטיפול", closed: "סגורה" };
  const priorityColors = { low: C.light, normal: C.blue, high: C.amber, urgent: C.red };
  const priorityLabels = { low: "נמוכה", normal: "רגילה", high: "גבוהה", urgent: "דחופה" };
  const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };

  // עדכון מקומי מיידי של פנייה — בלי לרענן את כל הרשימה (מונע "טעינה של שעות")
  const patchTicket = (id, patch) => {
    setLocalTickets(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
    setSelectedTicket(prev => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  const ticketStats = {
    total: localTickets.length,
    open: localTickets.filter(t => t.status === "open").length,
    inProgress: localTickets.filter(t => t.status === "in-progress").length,
    closed: localTickets.filter(t => t.status === "closed").length,
    urgent: localTickets.filter(t => t.priority === "urgent").length,
  };

  const filteredAll = localTickets
    .filter(t => {
      const matchSearch = `${t.name} ${t.email} ${t.subject} ${t.message} ${t.id}`.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === "all" || t.status === filterStatus;
      return matchSearch && matchStatus;
    })
    .sort((a, b) => {
      if (sortBy === "date_desc") return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === "date_asc") return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === "priority") return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
      return 0;
    });
  const totalCount = filteredAll.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const filtered = filteredAll.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const handleReply = async () => {
    const editor = editorRef.current;
    const html = editor ? editor.innerHTML : "";
    const text = (editor ? (editor.innerText || "") : replyText).trim();
    if (!selectedTicket || !text) return;
    const ticketId = selectedTicket.id;
    const endpoint = (sendViaEmail && selectedTicket.email)
      ? `/emails/reply-ticket/${ticketId}` : `/tickets/${ticketId}/reply`;
    setReplyText('');
    setReplyDialogOpen(false);
    if (editor) editor.innerHTML = "";
    try {
      const res = await api.post(endpoint, { message: text, html, from: fromAddress || undefined });
      if (res.status === 'success') {
        if (sendViaEmail && selectedTicket.email) {
          snack(res.emailSent ? 'תגובה נשלחה + מייל נשלח ללקוח' : 'תגובה נשמרה (מייל לא נשלח)', res.emailSent ? 'success' : 'warning');
        } else {
          snack('תגובה נשלחה', 'success');
        }
        // רענון הפנייה הבודדת בלבד (מהיר) + עדכון מקומי ברשימה
        const updated = await api.get(`/tickets/${ticketId}`);
        setSelectedTicket(updated);
        setLocalTickets(prev => prev.map(t => (t.id === ticketId ? updated : t)));
      } else {
        snack('שגיאה בשליחת התגובה', 'error');
      }
    } catch {
      snack('שגיאה בשליחת התגובה', 'error');
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    patchTicket(ticketId, { status: newStatus }); // עדכון מיידי
    snack('הסטטוס עודכן', 'success');
    try { await api.put(`/tickets/${ticketId}`, { status: newStatus }); }
    catch { snack('שגיאה בעדכון הסטטוס', 'error'); }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    patchTicket(ticketId, { priority: newPriority });
    snack('העדיפות עודכנה', 'success');
    try { await api.put(`/tickets/${ticketId}`, { priority: newPriority }); }
    catch { snack('שגיאה בעדכון העדיפות', 'error'); }
  };

  const handleCreateTicket = async () => {
    try {
      const res = await api.post("/tickets", newTicket);
      if (res.status === "success") {
        snack("פנייה נוצרה", "success");
        setNewTicketDialog(false);
        const created = res.ticket || {
          id: res.id || `T-${Date.now().toString(36).toUpperCase()}`,
          ...newTicket, status: "open", priority: "normal", replies: [],
          createdAt: new Date().toLocaleString("he-IL"),
        };
        setLocalTickets(prev => [created, ...prev]);
        setNewTicket({ name: "", email: "", phone: "", subject: "", message: "" });
      }
    } catch { snack('שגיאה ביצירת הפנייה', 'error'); }
  };

  const handleAssignAgent = async (ticketId, agentId) => {
    const agent = agents?.find(a => a.id === agentId);
    patchTicket(ticketId, { assignedTo: agentId || null, assignedToName: agent?.name || null });
    snack(agentId ? 'הפנייה שויכה לנציג' : 'בוטל שיוך הנציג', 'success');
    try { await api.put(`/tickets/${ticketId}`, { assignedTo: agentId || null }); }
    catch { snack('שגיאה בשיוך', 'error'); }
  };

  const handleLinkClient = async (ticketId, clientId) => {
    const client = clients?.find(c => c.user_id === clientId);
    const clientName = client ? (`${client.first_name || ""} ${client.last_name || ""}`.trim() || client.email) : null;
    patchTicket(ticketId, { linkedClient: clientId || null, linkedClientName: clientName });
    snack(clientId ? 'קושר ללקוח' : 'בוטל הקישור', 'success');
    try { await api.put(`/tickets/${ticketId}`, { linkedClient: clientId || null }); }
    catch { snack('שגיאה בקישור', 'error'); }
  };

  const handleDeleteTicket = async (id) => {
    setLocalTickets(prev => prev.filter(t => t.id !== id)); // הסרה מיידית
    if (selectedTicket?.id === id) setSelectedTicket(null);
    snack("פנייה נמחקה", "success");
    try { await api.del(`/tickets/${id}`); }
    catch { snack('שגיאה במחיקה', 'error'); }
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      {!selectedTicket && (
      <>
        {/* Toolbar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>📩 פניות</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" placeholder="חיפוש בנושא / תוכן / מייל לקוח..." value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
              sx={{ width: 300, "& .MuiOutlinedInput-root": { borderRadius: 5, background: C.white } }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ borderRadius: 3, background: C.white }}>
                <MenuItem value="date_desc">חדש ראשון</MenuItem>
                <MenuItem value="date_asc">ישן ראשון</MenuItem>
                <MenuItem value="priority">לפי עדיפות</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewTicketDialog(true)}
              sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>פנייה חדשה</Button>
            <Tooltip title="רענון מלא"><IconButton onClick={onRefresh}><RefreshIcon /></IconButton></Tooltip>
          </Box>
        </Box>

        {/* Status tabs + pagination */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[
              { key: "open", label: "חדשות", count: ticketStats.open, color: C.amber },
              { key: "in-progress", label: "בטיפול", count: ticketStats.inProgress, color: C.blue },
              { key: "closed", label: "סגורות", count: ticketStats.closed, color: C.green },
              { key: "all", label: "הכל", count: ticketStats.total, color: C.primary },
            ].map(tb => {
              const active = filterStatus === tb.key;
              return (
                <Box key={tb.key} onClick={() => setFilterStatus(tb.key)} sx={{
                  px: 1.75, py: .75, borderRadius: 2.5, cursor: "pointer", display: "flex", alignItems: "center", gap: .75,
                  border: `1px solid ${active ? tb.color : C.border}`, background: active ? tb.color + "12" : C.white,
                  transition: "all .15s", "&:hover": { borderColor: tb.color + "88" },
                }}>
                  <Typography sx={{ fontSize: ".82rem", fontWeight: 700, color: active ? tb.color : C.mid }}>{tb.label}</Typography>
                  <Box sx={{ minWidth: 20, textAlign: "center", px: .6, borderRadius: 10, fontSize: ".7rem", fontWeight: 800,
                    background: active ? tb.color : C.bg, color: active ? "#fff" : C.light }}>{tb.count}</Box>
                </Box>
              );
            })}
          </Box>
          {totalCount > PAGE_SIZE && (
            <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
              <Typography sx={{ fontSize: ".78rem", color: C.light, mr: .5 }}>
                {page * PAGE_SIZE + 1}–{Math.min(totalCount, (page + 1) * PAGE_SIZE)} מתוך {totalCount}
              </Typography>
              <Button size="small" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{ minWidth: 34, borderRadius: 2, color: C.mid }}>‹</Button>
              <Button size="small" disabled={page >= pageCount - 1} onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                sx={{ minWidth: 34, borderRadius: 2, color: C.mid }}>›</Button>
            </Box>
          )}
        </Box>
      </>
      )}

      <Grid container spacing={2.5}>
        {/* Tickets list — סגנון תאגידי, נפתחת בלחיצה */}
        {!selectedTicket && (
        <Grid item xs={12}>
          <Box>
            {filtered.map((ticket) => {
              const st = statusColors[ticket.status];
              return (
                <Box key={ticket.id} onClick={() => setSelectedTicket(ticket)} sx={{
                  display: "flex", alignItems: "center", gap: 2, px: 2, py: 1.5,
                  mb: 1.25, borderRadius: 3, background: C.white, border: `1px solid ${C.border}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,.04)",
                  cursor: "pointer", transition: "all .18s",
                  "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,.09)", transform: "translateY(-2px)", borderColor: C.primary + "44" },
                }}>
                  {/* אווטאר + שם + מייל (קצה ימין) */}
                  <Box sx={{ width: 200, flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#eef1f6", color: C.light, flexShrink: 0 }}><PersonIcon sx={{ fontSize: 20 }} /></Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: ".82rem", fontWeight: 800, color: C.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.name || "לא ידוע"}</Typography>
                      <Typography sx={{ fontSize: ".68rem", color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.email || ""}</Typography>
                    </Box>
                  </Box>
                  {/* נושא — מיושר לימין (ברירת מחדל RTL), בלי Re: */}
                  <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
                    <Typography sx={{ fontSize: ".95rem", fontWeight: 700, color: C.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(ticket.subject || "").replace(/^((re|fwd|fw|השב|העבר):\s*)+/i, "") || "ללא נושא"}
                    </Typography>
                  </Box>
                  {/* אינדיקטורים */}
                  <Box sx={{ width: 180, flexShrink: 0, display: "flex", flexDirection: "column", gap: .35 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: st, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: ".68rem", fontWeight: 700, color: st }}>{statusLabels[ticket.status]}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                      <PersonIcon sx={{ fontSize: 14, color: ticket.assignedToName ? C.primary : C.light }} />
                      <Typography sx={{ fontSize: ".68rem", color: ticket.assignedToName ? C.mid : C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ticket.assignedToName || "לא משויך לנציג"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                      <LinkIcon sx={{ fontSize: 14, color: ticket.linkedClientName ? C.green : C.light }} />
                      <Typography sx={{ fontSize: ".68rem", color: ticket.linkedClientName ? C.mid : C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ticket.linkedClientName || "לא מקושר ללקוח"}
                      </Typography>
                    </Box>
                  </Box>
                  {/* מס' פנייה + תאריך (קצה שמאל) */}
                  <Box sx={{ width: 104, flexShrink: 0, textAlign: "center" }}>
                    <Typography sx={{ fontSize: ".72rem", fontWeight: 800, color: C.mid, fontFamily: "monospace" }}>{ticket.id?.replace(/^T-/, "") || "—"}</Typography>
                    <Typography sx={{ fontSize: ".62rem", color: C.light, whiteSpace: "nowrap" }}>{ticket.createdAt}</Typography>
                  </Box>
                </Box>
              );
            })}
            {filtered.length === 0 && <Typography sx={{ color: C.light, textAlign: "center", py: 5 }}>אין פניות</Typography>}
          </Box>
        </Grid>
        )}

        {/* Selected ticket detail — עיצוב שיחה מודרני */}
        {selectedTicket && (
          <Grid item xs={12}>
            <StyledCard sx={{ overflow: "hidden", p: 0, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 210px)" }}>
              {/* Light header */}
              <Box sx={{ background: C.white, borderBottom: `1px solid ${C.border}`, px: 2.5, py: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Tooltip title="חזרה לרשימה">
                    <IconButton size="small" onClick={() => setSelectedTicket(null)} sx={{ color: C.mid, bgcolor: C.bg, "&:hover": { bgcolor: C.border } }}>
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Avatar sx={{ bgcolor: "#eef1f6", color: C.mid, fontWeight: 700, width: 46, height: 46, fontSize: ".95rem", flexShrink: 0, border: `1px solid ${C.border}` }}>
                    {getInitials(selectedTicket.name || selectedTicket.email)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.dark, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis" }}>{(selectedTicket.subject || "").replace(/^((re|fwd|fw|השב|העבר):\s*)+/i, "") || "ללא נושא"}</Typography>
                    <Box sx={{ display: "flex", gap: .75, flexWrap: "wrap", mt: .5, alignItems: "center" }}>
                      <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: C.dark }}>{selectedTicket.name || "לא ידוע"}</Typography>
                      {selectedTicket.email && <Chip icon={<EmailIcon sx={{ fontSize: 13 }} />} label={selectedTicket.email} size="small" sx={{ height: 22, fontSize: ".68rem", color: C.mid, bgcolor: C.bg }} />}
                      {selectedTicket.phone && <Chip icon={<PhoneIcon sx={{ fontSize: 13 }} />} label={selectedTicket.phone} size="small" sx={{ height: 22, fontSize: ".68rem", color: C.mid, bgcolor: C.bg }} />}
                      <Chip label={`#${selectedTicket.id}`} size="small" sx={{ height: 22, fontSize: ".66rem", color: C.light, bgcolor: C.bg, fontFamily: "monospace" }} />
                    </Box>
                  </Box>
                  <Tooltip title="מחק פנייה">
                    <IconButton size="small" onClick={() => handleDeleteTicket(selectedTicket.id)} sx={{ color: C.red }}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Controls toolbar */}
              <Box sx={{ display: "flex", gap: 1, px: 2, py: 1.5, flexWrap: "wrap", borderBottom: `1px solid ${C.border}`, background: C.white }}>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select value={selectedTicket.status} onChange={e => handleStatusChange(selectedTicket.id, e.target.value)}
                    sx={{ borderRadius: 3, fontSize: ".8rem", fontWeight: 700, color: statusColors[selectedTicket.status], "& fieldset": { borderColor: statusColors[selectedTicket.status] + "55" } }}>
                    <MenuItem value="open">🟠 פתוחה</MenuItem>
                    <MenuItem value="in-progress">🔵 בטיפול</MenuItem>
                    <MenuItem value="closed">🟢 סגורה</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select value={selectedTicket.priority} onChange={e => handlePriorityChange(selectedTicket.id, e.target.value)}
                    sx={{ borderRadius: 3, fontSize: ".8rem", fontWeight: 700, color: priorityColors[selectedTicket.priority] }}>
                    <MenuItem value="low">עדיפות נמוכה</MenuItem>
                    <MenuItem value="normal">עדיפות רגילה</MenuItem>
                    <MenuItem value="high">עדיפות גבוהה</MenuItem>
                    <MenuItem value="urgent">🔥 דחופה</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<AssignmentIndIcon />} onClick={() => { setAgentQuery(""); setAssignDialogOpen(true); }}
                  sx={{ borderRadius: 3, fontSize: ".8rem", fontWeight: 700, textTransform: "none", borderColor: C.border,
                    color: selectedTicket.assignedToName ? C.primary : C.mid, bgcolor: selectedTicket.assignedToName ? C.primaryLight : "transparent" }}>
                  {selectedTicket.assignedToName || "שייך נציג"}
                </Button>
                <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => { setClientQuery(""); setLinkDialogOpen(true); }}
                  sx={{ borderRadius: 3, fontSize: ".8rem", fontWeight: 700, textTransform: "none", borderColor: C.border,
                    color: selectedTicket.linkedClientName ? C.green : C.mid, bgcolor: selectedTicket.linkedClientName ? C.greenBg : "transparent" }}>
                  {selectedTicket.linkedClientName || "קשר ללקוח"}
                </Button>
                {/* כפתור תגובה — בקצה שמאל, הצד ההפוך משיוך הנציג */}
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<ReplyIcon />}
                  onClick={() => { setReplySubject(`Re: ${(selectedTicket.subject || "").replace(/^((re|fwd|fw|השב|העבר):\s*)+/i, "")}`); setReplyText(""); setReplyDialogOpen(true); }}
                  sx={{ borderRadius: 3, fontSize: ".82rem", fontWeight: 800, px: 2.5, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`, boxShadow: "0 4px 14px rgba(124,58,237,.3)" }}>
                  תגובה
                </Button>
              </Box>

              {/* Conversation thread */}
              <Box ref={threadRef} sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2.5, background: C.bg, minHeight: 260 }}>
                {(() => {
                  const convo = [
                    { id: "orig", dir: selectedTicket.source === "email-out" ? "out" : "in", author: selectedTicket.source === "email-out" ? (selectedTicket.assignedToName || "תמיכה") : selectedTicket.name, text: selectedTicket.message, date: selectedTicket.createdAt, viaEmail: selectedTicket.source === "email" || selectedTicket.source === "email-out" },
                    ...(selectedTicket.replies || []).map((r, i) => ({
                      id: r.id || `r${i}`,
                      dir: (r.agentId || (r.author && r.author !== selectedTicket.name)) ? "out" : "in",
                      author: r.author,
                      text: r.message,
                      date: r.createdAt,
                      viaEmail: r.isEmail,
                      emailSent: r.emailSent,
                    })),
                  ];
                  // סדר כרונולוגי: ישן למעלה, חדש למטה
                  const tsOf = (d) => { const t = Date.parse(String(d || "").replace(" ", "T")); return isNaN(t) ? 0 : t; };
                  convo.sort((a, b) => tsOf(a.date) - tsOf(b.date));
                  const OUR_EMAIL = "cs@clickerim.co.il";
                  return convo.map((m, idx) => {
                    const isOut = m.dir === "out";
                    const { main, quoted } = splitQuote(m.text);
                    const open = !!expandedQuotes[m.id];
                    const accent = isOut ? C.green : C.blue;      // אנחנו=ירוק, לקוח=כחול
                    const fromAddr = isOut ? OUR_EMAIL : (selectedTicket.email || "—");
                    const toAddr = isOut ? (selectedTicket.email || "—") : OUR_EMAIL;
                    return (
                      // לקוח (in) בצד ימין, אנחנו (out) בצד שמאל — כרטיס לכל הודעה
                      <Box key={m.id} sx={{ display: "flex", justifyContent: isOut ? "flex-end" : "flex-start", mb: 2.5 }}>
                        <Box sx={{
                          width: "92%", maxWidth: 760, background: C.white, borderRadius: 3, overflow: "hidden",
                          boxShadow: "0 4px 16px rgba(0,0,0,.06)",
                          border: `1px solid ${C.border}`,
                        }}>
                          {/* Card header — מאת/אל/תאריך/מס' */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.25,
                            background: `linear-gradient(90deg, ${accent}14, transparent)`, borderBottom: `1px solid ${C.border}` }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: accent, color: "#fff", fontSize: ".74rem", fontWeight: 800, flexShrink: 0 }}>
                              {isOut ? "CS" : getInitials(m.author)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: .6, flexWrap: "wrap" }}>
                                <Typography sx={{ fontSize: ".88rem", fontWeight: 800, color: C.dark }}>{isOut ? (m.author || "תמיכה") : (m.author || "לקוח")}</Typography>
                                <Chip label={isOut ? "נציג" : "לקוח"} size="small" sx={{ height: 18, fontSize: ".6rem", fontWeight: 700, bgcolor: accent + "1f", color: accent }} />
                                {m.viaEmail && <EmailIcon sx={{ fontSize: 13, color: m.emailSent === false ? C.amber : C.light }} />}
                              </Box>
                              <Typography sx={{ fontSize: ".68rem", color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", mt: .2 }}>
                                מאת: {fromAddr} ← אל: {toAddr}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: "left", flexShrink: 0 }}>
                              <Typography sx={{ fontSize: ".68rem", color: C.mid, fontWeight: 800, fontFamily: "monospace" }}>#{idx + 1}</Typography>
                              <Typography sx={{ fontSize: ".64rem", color: C.light, whiteSpace: "nowrap" }}>{m.date}</Typography>
                            </Box>
                          </Box>
                          {/* Card body */}
                          <Box sx={{ px: 2.25, py: 1.75 }}>
                            <Typography sx={{ fontSize: ".92rem", whiteSpace: "pre-wrap", lineHeight: 1.7, wordBreak: "break-word", color: C.dark }}>
                              {main || <span style={{ color: C.light }}>(ללא תוכן חדש)</span>}
                            </Typography>
                            {quoted && (
                              <>
                                <Box onClick={() => setExpandedQuotes(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                                  sx={{ mt: 1.25, display: "inline-flex", alignItems: "center", gap: .3, cursor: "pointer",
                                    color: C.light, fontSize: ".72rem", fontWeight: 600, "&:hover": { color: C.mid } }}>
                                  <ExpandMoreIcon sx={{ fontSize: 16, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
                                  {open ? "הסתר הודעות קודמות" : "הצג הודעות קודמות"}
                                </Box>
                                <Collapse in={open}>
                                  <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, background: C.bg, border: `1px solid ${C.border}`, borderRight: `3px solid ${accent}66` }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: .5, mb: .75 }}>
                                      <HistoryIcon sx={{ fontSize: 14, color: C.light }} />
                                      <Typography sx={{ fontSize: ".68rem", fontWeight: 700, color: C.light }}>הודעה קודמת בשרשור</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: ".83rem", color: C.mid, whiteSpace: "pre-wrap", lineHeight: 1.75, wordBreak: "break-word" }}>{quoted}</Typography>
                                  </Box>
                                </Collapse>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>

            </StyledCard>
          </Grid>
        )}
      </Grid>

      {/* New Ticket Dialog */}
      <Dialog open={newTicketDialog} onClose={() => setNewTicketDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>📝 יצירת פנייה חדשה</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={6}><TextField fullWidth label="שם" value={newTicket.name} onChange={e => setNewTicket({ ...newTicket, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="טלפון" value={newTicket.phone} onChange={e => setNewTicket({ ...newTicket, phone: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="אימייל" value={newTicket.email} onChange={e => setNewTicket({ ...newTicket, email: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="נושא" value={newTicket.subject} onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="הודעה" multiline rows={3} value={newTicket.message} onChange={e => setNewTicket({ ...newTicket, message: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNewTicketDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleCreateTicket} sx={{ borderRadius: 3, fontWeight: 700 }}>צור פנייה</Button>
        </DialogActions>
      </Dialog>

      {/* Reply dialog — כתיבת תגובה בסגנון מייל */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        {/* כותרת בסגנון כרטיס בשרשור (נציג/ירוק) */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 1.75,
          background: `linear-gradient(90deg, ${C.green}14, transparent)`, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: C.green, color: "#fff", fontSize: ".74rem", fontWeight: 800 }}>CS</Avatar>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: .6 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: C.dark, lineHeight: 1.2 }}>תגובה ללקוח</Typography>
                <Chip label="נציג" size="small" sx={{ height: 18, fontSize: ".6rem", fontWeight: 700, bgcolor: C.green + "1f", color: C.green }} />
              </Box>
              <Typography sx={{ fontSize: ".72rem", color: C.light }}>{selectedTicket?.name || ""}</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setReplyDialogOpen(false)} sx={{ color: C.mid }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {/* שדות: מאת / אל / נושא */}
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: .85, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: ".78rem", fontWeight: 700, color: C.mid, minWidth: 44 }}>מאת</Typography>
                <Select variant="standard" disableUnderline value={fromAddress} onChange={e => setFromAddress(e.target.value)}
                  sx={{ fontSize: ".85rem", color: C.dark, minWidth: 200 }}>
                  {senders.length === 0 && <MenuItem value="">ברירת מחדל</MenuItem>}
                  {senders.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </Box>
              <Box sx={{ width: "1px", height: 22, bgcolor: C.border }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: ".78rem", fontWeight: 700, color: C.mid }}>אל</Typography>
                <Typography sx={{ fontSize: ".85rem", color: C.dark }}>{selectedTicket?.email || "— (תגובה פנימית)"}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: .85, borderBottom: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: ".78rem", fontWeight: 700, color: C.mid, minWidth: 44 }}>נושא</Typography>
              <TextField fullWidth variant="standard" value={replySubject} onChange={e => setReplySubject(e.target.value)}
                InputProps={{ disableUnderline: true, sx: { fontSize: ".85rem" } }} />
            </Box>
          </Box>
          {/* סרגל עיצוב */}
          <Box sx={{ display: "flex", alignItems: "center", gap: .5, px: 2, py: .5, background: C.bg, borderBottom: `1px solid ${C.border}` }}>
            {[
              { cmd: "bold", icon: <FormatBoldIcon sx={{ fontSize: 19 }} />, title: "מודגש" },
              { cmd: "italic", icon: <FormatItalicIcon sx={{ fontSize: 19 }} />, title: "נטוי" },
              { cmd: "underline", icon: <FormatUnderlinedIcon sx={{ fontSize: 19 }} />, title: "קו תחתון" },
            ].map(b => (
              <Tooltip key={b.cmd} title={b.title}>
                <IconButton size="small" onMouseDown={e => { e.preventDefault(); document.execCommand(b.cmd, false); editorRef.current?.focus(); }}
                  sx={{ color: C.mid, borderRadius: 1.5, "&:hover": { bgcolor: C.primaryLight, color: C.primary } }}>
                  {b.icon}
                </IconButton>
              </Tooltip>
            ))}
            <Tooltip title="רשימה">
              <IconButton size="small" onMouseDown={e => { e.preventDefault(); document.execCommand("insertUnorderedList", false); editorRef.current?.focus(); }}
                sx={{ color: C.mid, borderRadius: 1.5, fontSize: "1rem", "&:hover": { bgcolor: C.primaryLight, color: C.primary } }}>•</IconButton>
            </Tooltip>
          </Box>
          {/* עורך תוכן */}
          <Box component="div" contentEditable suppressContentEditableWarning ref={editorRef} dir="rtl"
            onInput={e => setReplyText(e.currentTarget.innerText)}
            sx={{ minHeight: 190, maxHeight: 300, overflowY: "auto", px: 3, py: 2, fontSize: ".92rem", lineHeight: 1.7, color: C.dark, outline: "none",
              "&:empty:before": { content: '"כתוב את תוכן התגובה..."', color: C.light } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${C.border}`, background: C.white, justifyContent: "space-between" }}>
          {selectedTicket?.email ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
              <Switch size="small" checked={sendViaEmail} onChange={e => setSendViaEmail(e.target.checked)} />
              <Typography sx={{ fontSize: ".75rem", color: sendViaEmail ? C.primary : C.light, fontWeight: 600 }}>
                {sendViaEmail ? "📧 יישלח במייל ללקוח" : "תגובה פנימית בלבד"}
              </Typography>
            </Box>
          ) : <Box />}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setReplyDialogOpen(false)} sx={{ borderRadius: 3, color: C.mid, fontWeight: 600 }}>ביטול</Button>
            <Button variant="contained" onClick={handleReply} disabled={!replyText.trim()} startIcon={<SendIcon />}
              sx={{ borderRadius: 3, fontWeight: 700, px: 3, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`,
                "&.Mui-disabled": { background: C.border, color: C.light, boxShadow: "none" } }}>שלח תגובה</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Assign agent dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
          <AssignmentIndIcon sx={{ color: C.primary }} /> שיוך נציג לפנייה
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" autoFocus placeholder="חיפוש נציג..." value={agentQuery} onChange={e => setAgentQuery(e.target.value)}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }} />
          <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
            <Box onClick={() => { if (selectedTicket) handleAssignAgent(selectedTicket.id, ""); setAssignDialogOpen(false); }}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 2, cursor: "pointer", "&:hover": { bgcolor: C.bg } }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: C.bg, color: C.light }}><CloseIcon sx={{ fontSize: 18 }} /></Avatar>
              <Typography sx={{ fontSize: ".88rem", color: C.mid }}>ללא שיוך</Typography>
              {selectedTicket && !selectedTicket.assignedTo && <CheckCircleIcon sx={{ color: C.green, ml: "auto", fontSize: 20 }} />}
            </Box>
            {(agents || []).filter(a => `${a.name || ""} ${a.username || ""}`.toLowerCase().includes(agentQuery.toLowerCase())).map(a => {
              const active = selectedTicket?.assignedTo === a.id;
              return (
                <Box key={a.id} onClick={() => { handleAssignAgent(selectedTicket.id, a.id); setAssignDialogOpen(false); }}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 2, cursor: "pointer", bgcolor: active ? C.primaryLight : "transparent", "&:hover": { bgcolor: active ? C.primaryLight : C.bg } }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: "#eef1f6", color: C.mid, fontSize: ".78rem", fontWeight: 700 }}>{getInitials(a.name)}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: ".88rem", fontWeight: 700, color: C.dark }}>{a.name}</Typography>
                    {a.username && <Typography sx={{ fontSize: ".72rem", color: C.light }}>{a.username}</Typography>}
                  </Box>
                  {active && <CheckCircleIcon sx={{ color: C.green, ml: "auto", fontSize: 20 }} />}
                </Box>
              );
            })}
            {(agents || []).length === 0 && <Typography sx={{ textAlign: "center", color: C.light, py: 3, fontSize: ".85rem" }}>אין נציגים</Typography>}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Link client dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
          <LinkIcon sx={{ color: C.green }} /> קישור פנייה ללקוח
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" autoFocus placeholder="חיפוש לפי שם / מייל / טלפון..." value={clientQuery} onChange={e => setClientQuery(e.target.value)}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }} />
          <Box sx={{ maxHeight: 340, overflowY: "auto" }}>
            <Box onClick={() => { if (selectedTicket) handleLinkClient(selectedTicket.id, ""); setLinkDialogOpen(false); }}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 2, cursor: "pointer", "&:hover": { bgcolor: C.bg } }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: C.bg, color: C.light }}><CloseIcon sx={{ fontSize: 18 }} /></Avatar>
              <Typography sx={{ fontSize: ".88rem", color: C.mid }}>ללא קישור</Typography>
              {selectedTicket && !selectedTicket.linkedClient && <CheckCircleIcon sx={{ color: C.green, ml: "auto", fontSize: 20 }} />}
            </Box>
            {(clients || []).map(c => ({ ...c, _name: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "" }))
              .filter(c => `${c._name} ${c.email || ""} ${c.phone || ""}`.toLowerCase().includes(clientQuery.toLowerCase())).slice(0, 100).map(c => {
              const active = selectedTicket?.linkedClient === c.user_id;
              const isMailMatch = selectedTicket?.email && (c.email || "").toLowerCase() === selectedTicket.email.toLowerCase();
              return (
                <Box key={c.user_id} onClick={() => { handleLinkClient(selectedTicket.id, c.user_id); setLinkDialogOpen(false); }}
                  sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: 2, cursor: "pointer", bgcolor: active ? C.greenBg : "transparent", "&:hover": { bgcolor: active ? C.greenBg : C.bg } }}>
                  <Avatar sx={{ width: 34, height: 34, bgcolor: "#eef1f6", color: C.mid, fontSize: ".78rem", fontWeight: 700 }}>{getInitials(c._name || c.email)}</Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: ".88rem", fontWeight: 700, color: C.dark }}>{c._name || c.email}</Typography>
                    {c.email && <Typography sx={{ fontSize: ".72rem", color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.email}</Typography>}
                  </Box>
                  {isMailMatch && <Chip label="התאמת מייל" size="small" sx={{ height: 18, fontSize: ".6rem", bgcolor: C.greenBg, color: C.green }} />}
                  {active && <CheckCircleIcon sx={{ color: C.green, ml: "auto", fontSize: 20 }} />}
                </Box>
              );
            })}
            {(clients || []).length === 0 && <Typography sx={{ textAlign: "center", color: C.light, py: 3, fontSize: ".85rem" }}>אין לקוחות</Typography>}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ PHONES PAGE ═══════════════════ */
const PhonesPage = ({ phones, loading, onRefresh, snack }) => {
  const [addDialog, setAddDialog] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [bulkNumbers, setBulkNumbers] = useState("");
  const [bulkAdding, setBulkAdding] = useState(false);

  const handleAdd = async () => {
    if (!newNumber.trim()) return;
    const res = await api.post("/phones", { number: newNumber, description: newDesc });
    if (res.status === "success") {
      snack("מספר נוסף", "success");
      setAddDialog(false);
      setNewNumber("");
      setNewDesc("");
      onRefresh();
    } else {
      snack(res.error || "שגיאה", "error");
    }
  };

  const handleBulkAdd = async () => {
    const numbers = bulkNumbers.split("\n").map(n => n.trim()).filter(n => n.length > 0);
    if (numbers.length === 0) return;
    setBulkAdding(true);
    let added = 0, failed = 0;
    for (const num of numbers) {
      try {
        const res = await api.post("/phones", { number: num, description: "" });
        if (res.status === "success") added++; else failed++;
      } catch { failed++; }
    }
    setBulkAdding(false);
    snack(`נוספו ${added} מספרים${failed > 0 ? `, ${failed} נכשלו` : ""}`, added > 0 ? "success" : "error");
    setBulkNumbers("");
    setAddDialog(false);
    setBulkMode(false);
    onRefresh();
  };

  const handleRelease = async (number) => {
    const res = await api.post(`/phones/${number}/release`);
    if (res.status === "success") {
      snack("מספר שוחרר", "success");
      onRefresh();
    }
  };

  const handleDelete = async (number) => {
    const res = await api.del(`/phones/${number}`);
    if (res.status === "success") {
      snack("מספר נמחק", "success");
      onRefresh();
    }
  };

  const free = phones.filter(p => p.status === "free").length;
  const busy = phones.filter(p => p.status === "buzy").length;

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>📞 מלאי מספרים ({phones.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            הוסף מספר
          </Button>
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}><StatCard icon={<PhoneIcon />} label="סה״כ" value={phones.length} color={C.primary} bg={C.primaryLight} /></Grid>
        <Grid item xs={4}><StatCard icon={<CheckCircleIcon />} label="פנויים" value={free} color={C.green} bg={C.greenBg} /></Grid>
        <Grid item xs={4}><StatCard icon={<WarningIcon />} label="תפוסים" value={busy} color={C.red} bg={C.redBg} /></Grid>
      </Grid>

      {/* Phones table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: C.bg }}>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>מספר</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>סטטוס</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>משחק</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>לקוח</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>תיאור</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>שימוש אחרון</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.dark }}>פעולות</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {phones.map((phone) => (
              <TableRow key={phone.number} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                <TableCell sx={{ fontWeight: 700, fontSize: ".95rem", fontFamily: "monospace" }}>{phone.number}</TableCell>
                <TableCell>
                  <Chip
                    label={phone.status === "free" ? "פנוי" : "תפוס"}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      background: phone.status === "free" ? C.greenBg : C.redBg,
                      color: phone.status === "free" ? C.green : C.red,
                      border: `1px solid ${phone.status === "free" ? C.green : C.red}33`
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: ".85rem", color: C.mid }}>{phone.gameName || "—"}</TableCell>
                <TableCell sx={{ fontSize: ".85rem", color: C.mid }}>{phone.userName || "—"}</TableCell>
                <TableCell sx={{ fontSize: ".85rem", color: C.light }}>{phone.description || "—"}</TableCell>
                <TableCell sx={{ fontSize: ".8rem", color: C.light }}>
                  {phone.lastUse ? new Date(phone.lastUse).toLocaleString("he-IL") : "—"}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: .5 }}>
                    {phone.status === "buzy" && (
                      <Tooltip title="שחרר מספר">
                        <IconButton size="small" onClick={() => handleRelease(phone.number)} sx={{ color: C.amber }}>
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="מחק">
                      <IconButton size="small" onClick={() => handleDelete(phone.number)} sx={{ color: C.red }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={addDialog} onClose={() => { setAddDialog(false); setBulkMode(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          ➕ הוספת מספרים
          <Button size="small" onClick={() => setBulkMode(!bulkMode)} sx={{ fontWeight: 700, borderRadius: 3 }}>
            {bulkMode ? "מספר בודד" : "הוספה מרובה"}
          </Button>
        </DialogTitle>
        <DialogContent>
          {bulkMode ? (
            <>
              <Typography sx={{ fontSize: ".85rem", color: C.light, mb: 1.5, mt: 1 }}>הכניסו מספר טלפון בכל שורה:</Typography>
              <TextField
                fullWidth multiline rows={8} value={bulkNumbers}
                onChange={e => setBulkNumbers(e.target.value)}
                placeholder={"0771234567\n0771234568\n0771234569"}
                sx={{ fontFamily: "monospace", "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
              {bulkNumbers.trim() && (
                <Typography sx={{ mt: 1, fontSize: ".82rem", color: C.primary, fontWeight: 600 }}>
                  {bulkNumbers.split("\n").filter(n => n.trim()).length} מספרים לייבוא
                </Typography>
              )}
            </>
          ) : (
            <>
              <TextField fullWidth label="מספר טלפון" value={newNumber} onChange={e => setNewNumber(e.target.value)} sx={{ mt: 2, mb: 2 }} placeholder="למשל: 0771234567" />
              <TextField fullWidth label="תיאור (אופציונלי)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setAddDialog(false); setBulkMode(false); }}>ביטול</Button>
          {bulkMode ? (
            <Button variant="contained" onClick={handleBulkAdd} disabled={bulkAdding} sx={{ borderRadius: 3, fontWeight: 700 }}>
              {bulkAdding ? <CircularProgress size={20} color="inherit" /> : `ייבא ${bulkNumbers.split("\n").filter(n => n.trim()).length} מספרים`}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleAdd} sx={{ borderRadius: 3, fontWeight: 700 }}>הוסף</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ AGENTS PAGE ═══════════════════ */
const AgentsPage = ({ agents, loading, onRefresh, snack }) => {
  const [search, setSearch] = useState("");
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);
  const [newAgent, setNewAgent] = useState({ name: "", username: "", password: "", role: "agent", createMailbox: false });
  const [editData, setEditData] = useState({});
  const [resetPw, setResetPw] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = agents.filter(a =>
    `${a.name} ${a.username} ${a.role} ${a.id}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async () => {
    if (!newAgent.name || !newAgent.username || !newAgent.password) return;
    setSaving(true);
    try {
      const res = await api.post("/agents", newAgent);
      if (res.status === "success") {
        const mailMsg = res.mailboxCreated ? ` + תיבת מייל ${newAgent.username}@clickerim.co.il` : '';
        snack(`נציג נוסף בהצלחה${mailMsg}`, "success");
        setAddDialog(false);
        setNewAgent({ name: "", username: "", password: "", role: "agent", createMailbox: false });
        onRefresh();
      } else {
        snack(res.error || "שגיאה", "error");
      }
    } catch { snack("שגיאת חיבור", "error"); }
    setSaving(false);
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/agents/${editDialog}`, editData);
      if (res.status === "success") {
        snack("נציג עודכן בהצלחה", "success");
        setEditDialog(null);
        onRefresh();
      } else {
        snack(res.error || "שגיאה", "error");
      }
    } catch { snack("שגיאת חיבור", "error"); }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!resetPw || resetPw.length < 4) { snack("סיסמה חייבת להכיל 4 תווים לפחות", "warning"); return; }
    setSaving(true);
    try {
      const res = await api.post(`/agents/${resetDialog}/reset-password`, { newPassword: resetPw });
      if (res.status === "success") {
        snack("סיסמה אופסה", "success");
        setResetDialog(null);
        setResetPw("");
      } else {
        snack(res.error || "שגיאה", "error");
      }
    } catch { snack("שגיאת חיבור", "error"); }
    setSaving(false);
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm("האם למחוק את הנציג?")) return;
    try {
      await api.del(`/agents/${agentId}`);
      snack("נציג נמחק", "success");
      onRefresh();
    } catch { snack("שגיאה", "error"); }
  };

  const handleToggleActive = async (agent) => {
    await api.put(`/agents/${agent.id}`, { active: !agent.active });
    onRefresh();
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>👥 ניהול נציגים ({filtered.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TextField size="small" placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
            sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            נציג חדש
          </Button>
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Agents Grid */}
      <Grid container spacing={2.5}>
        {filtered.map(agent => (
          <Grid item xs={12} sm={6} lg={4} key={agent.id}>
            <StyledCard>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{
                      width: 44, height: 44, fontWeight: 700,
                      background: agent.active ? `linear-gradient(135deg, ${C.primary}, #7c3aed)` : C.border,
                    }}>
                      {agent.name?.[0] || "?"}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: C.dark }}>{agent.name}</Typography>
                      <Typography sx={{ fontSize: ".78rem", color: C.light }}>@{agent.username}</Typography>
                      {agent.mailbox && (
                        <Chip icon={<MailOutlineIcon sx={{ fontSize: 12 }} />} label={agent.mailbox} size="small"
                          sx={{ fontSize: '.6rem', height: 18, mt: .3, bgcolor: C.blueBg, color: C.blue }} />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: .3 }}>
                    <Chip
                      label={agent.active ? "פעיל" : "מושבת"}
                      size="small"
                      onClick={() => handleToggleActive(agent)}
                      sx={{
                        cursor: "pointer", fontWeight: 600, fontSize: ".7rem",
                        background: agent.active ? C.greenBg : C.redBg,
                        color: agent.active ? C.green : C.red,
                        border: `1px solid ${agent.active ? C.green : C.red}33`,
                      }}
                    />
                  </Box>
                </Box>

                {/* Agent stats */}
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Box sx={{ flex: 1, textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                    <Typography sx={{ fontSize: ".65rem", color: C.light }}>פניות</Typography>
                    <Typography sx={{ fontWeight: 800, color: C.primary }}>{agent.stats?.totalAssigned || 0}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                    <Typography sx={{ fontSize: ".65rem", color: C.light }}>סגורות</Typography>
                    <Typography sx={{ fontWeight: 800, color: C.green }}>{agent.stats?.closedCount || 0}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                    <Typography sx={{ fontSize: ".65rem", color: C.light }}>תגובות</Typography>
                    <Typography sx={{ fontWeight: 800, color: C.amber }}>{agent.stats?.totalReplies || 0}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                    <Typography sx={{ fontSize: ".65rem", color: C.light }}>ממוצע (דק׳)</Typography>
                    <Typography sx={{ fontWeight: 800, color: C.mid }}>{agent.stats?.avgResponseMin || "—"}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ fontSize: ".72rem", color: C.light }}>
                    {agent.lastLogin ? `כניסה: ${agent.lastLogin}` : "לא התחבר"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: .3 }}>
                    <Tooltip title="עריכה"><IconButton size="small" onClick={() => {
                      setEditData({ name: agent.name, username: agent.username, role: agent.role });
                      setEditDialog(agent.id);
                    }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="איפוס סיסמה"><IconButton size="small" onClick={() => setResetDialog(agent.id)}><LockResetIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="מחיקה"><IconButton size="small" onClick={() => handleDelete(agent.id)} sx={{ color: C.red }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
        {filtered.length === 0 && (
          <Grid item xs={12}>
            <Typography sx={{ color: C.light, textAlign: "center", mt: 4 }}>אין נציגים</Typography>
          </Grid>
        )}
      </Grid>

      {/* Add Agent Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>➕ הוספת נציג חדש</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={6}><TextField fullWidth label="שם" value={newAgent.name} onChange={e => setNewAgent({ ...newAgent, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="שם משתמש" value={newAgent.username} onChange={e => setNewAgent({ ...newAgent, username: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="סיסמה" type="password" value={newAgent.password} onChange={e => setNewAgent({ ...newAgent, password: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>תפקיד</InputLabel>
                <Select value={newAgent.role} label="תפקיד" onChange={e => setNewAgent({ ...newAgent, role: e.target.value })}>
                  <MenuItem value="agent">נציג</MenuItem>
                  <MenuItem value="admin">מנהל</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newAgent.createMailbox}
                    onChange={e => setNewAgent({ ...newAgent, createMailbox: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '.9rem' }}>📧 צור תיבת מייל</Typography>
                    <Typography sx={{ fontSize: '.72rem', color: C.light }}>
                      {newAgent.username ? `${newAgent.username}@clickerim.co.il` : 'username@clickerim.co.il'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving} sx={{ borderRadius: 3, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : "הוסף נציג"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>✏️ עריכת נציג</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={6}><TextField fullWidth label="שם" value={editData.name || ""} onChange={e => setEditData({ ...editData, name: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="שם משתמש" value={editData.username || ""} onChange={e => setEditData({ ...editData, username: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>תפקיד</InputLabel>
                <Select value={editData.role || "agent"} label="תפקיד" onChange={e => setEditData({ ...editData, role: e.target.value })}>
                  <MenuItem value="agent">נציג</MenuItem>
                  <MenuItem value="admin">מנהל</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(null)}>ביטול</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving} sx={{ borderRadius: 3, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : "שמור"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetDialog} onClose={() => { setResetDialog(null); setResetPw(""); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🔑 איפוס סיסמה</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="סיסמה חדשה" type="password" value={resetPw}
            onChange={e => setResetPw(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setResetDialog(null); setResetPw(""); }}>ביטול</Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={saving} sx={{ borderRadius: 3, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : "אפס סיסמה"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ RICH TEXT EDITOR ═══════════════════ */
const RichTextEditor = ({ value, onChange, minHeight = 200 }) => {
  const editorRef = useRef(null);
  const skipEffect = useRef(false);

  useEffect(() => {
    if (editorRef.current && !skipEffect.current) {
      if (editorRef.current.innerHTML !== (value || "")) {
        editorRef.current.innerHTML = value || "";
      }
    }
    skipEffect.current = false;
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current.focus();
    document.execCommand(cmd, false, val);
    skipEffect.current = true;
    onChange(editorRef.current.innerHTML);
  };

  const handleInput = () => {
    skipEffect.current = true;
    onChange(editorRef.current.innerHTML);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API_URL}/crm/upload`, {
        method: "POST",
        headers: { "x-crm-token": api.token || "" },
        body: formData,
      });
      const data = await res.json();
      return data.url || null;
    } catch { return null; }
  };

  const insertVideo = () => {
    const url = window.prompt("הזן כתובת וידאו (YouTube / קישור ישיר):");
    if (!url) return;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) {
      exec("insertHTML", `<div style="position:relative;padding-bottom:56.25%;margin:12px 0;"><iframe style="position:absolute;top:0;left:0;width:100%;height:100%;border-radius:8px;border:none;" src="https://www.youtube.com/embed/${yt[1]}" allowfullscreen></iframe></div>`);
    } else {
      exec("insertHTML", `<video src="${url}" controls style="max-width:100%;border-radius:8px;display:block;margin:8px 0;"></video>`);
    }
  };

  const TB = ({ onClick, title, children }) => (
    <Tooltip title={title} placement="top">
      <IconButton size="small" onMouseDown={e => { e.preventDefault(); onClick(); }}
        sx={{ borderRadius: 1.5, color: C.mid, p: .5, "&:hover": { bgcolor: C.primaryLight, color: C.primary } }}>
        {children}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", "&:focus-within": { borderColor: C.primary } }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: .2, p: .8, borderBottom: `1px solid ${C.border}`, flexWrap: "wrap", bgcolor: C.bg }}>
        <TB onClick={() => exec("bold")} title="מודגש"><FormatBoldIcon sx={{ fontSize: 18 }} /></TB>
        <TB onClick={() => exec("italic")} title="נטוי"><FormatItalicIcon sx={{ fontSize: 18 }} /></TB>
        <TB onClick={() => exec("underline")} title="קו תחתון"><FormatUnderlinedIcon sx={{ fontSize: 18 }} /></TB>
        <Divider orientation="vertical" flexItem sx={{ mx: .4, my: .5 }} />
        <TB onClick={() => exec("insertUnorderedList")} title="רשימת נקודות"><FormatListBulletedIcon sx={{ fontSize: 18 }} /></TB>
        <TB onClick={() => exec("insertOrderedList")} title="רשימה ממוספרת"><FormatListNumberedIcon sx={{ fontSize: 18 }} /></TB>
        <Divider orientation="vertical" flexItem sx={{ mx: .4, my: .5 }} />
        <TB onClick={() => exec("justifyRight")} title="יישור ימין"><FormatAlignRightIcon sx={{ fontSize: 18 }} /></TB>
        <TB onClick={() => exec("justifyCenter")} title="מרכז"><FormatAlignCenterIcon sx={{ fontSize: 18 }} /></TB>
        <TB onClick={() => exec("justifyLeft")} title="יישור שמאל"><FormatAlignLeftIcon sx={{ fontSize: 18 }} /></TB>
        <Divider orientation="vertical" flexItem sx={{ mx: .4, my: .5 }} />
        <TB onClick={() => { const u = window.prompt("הזן URL:"); if (u) exec("createLink", u); }} title="הוסף קישור">
          <LinkIcon sx={{ fontSize: 18 }} />
        </TB>
        <Tooltip title="העלה תמונה / וידאו" placement="top">
          <IconButton size="small" component="label"
            sx={{ borderRadius: 1.5, color: C.mid, p: .5, "&:hover": { bgcolor: C.primaryLight, color: C.primary } }}>
            <ImageIcon sx={{ fontSize: 18 }} />
            <input type="file" accept="image/*,video/mp4,video/webm" hidden onChange={async e => {
              const f = e.target.files[0];
              if (!f) return;
              const url = await uploadFile(f);
              if (url) {
                if (f.type.startsWith("video/"))
                  exec("insertHTML", `<video src="${url}" controls style="max-width:100%;border-radius:8px;display:block;margin:8px 0;"></video>`);
                else
                  exec("insertHTML", `<img src="${url}" style="max-width:100%;height:auto;border-radius:8px;display:block;margin:8px 0;" />`);
              }
              e.target.value = "";
            }} />
          </IconButton>
        </Tooltip>
        <TB onClick={insertVideo} title="הטמע וידאו YouTube"><OndemandVideoIcon sx={{ fontSize: 18 }} /></TB>
        <Divider orientation="vertical" flexItem sx={{ mx: .4, my: .5 }} />
        <Select size="small" defaultValue="3" onMouseDown={e => e.stopPropagation()}
          onChange={e => exec("fontSize", e.target.value)}
          sx={{ fontSize: ".72rem", height: 28, "& .MuiSelect-select": { py: .3, pr: 3, pl: 1 } }}>
          {[["1","קטן"],["2","רגיל"],["3","בינוני"],["4","גדול"],["5","XL"],["6","XXL"]].map(([v,l]) => (
            <MenuItem key={v} value={v} sx={{ fontSize: ".82rem" }}>{l}</MenuItem>
          ))}
        </Select>
        <Tooltip title="צבע טקסט">
          <Box component="input" type="color" defaultValue="#374151"
            onMouseDown={e => e.stopPropagation()}
            onChange={e => exec("foreColor", e.target.value)}
            style={{ width: 28, height: 28, border: `1px solid ${C.border}`, cursor: "pointer", borderRadius: 6, padding: 2 }} />
        </Tooltip>
      </Box>
      {/* Editor */}
      <Box ref={editorRef} contentEditable suppressContentEditableWarning
        onInput={handleInput} dir="rtl"
        sx={{ p: 2.5, minHeight, outline: "none", color: C.dark, lineHeight: 1.9, fontSize: "15px",
          fontFamily: "Arial, sans-serif",
          "& img": { maxWidth: "100%", borderRadius: "8px" },
          "& video": { maxWidth: "100%", borderRadius: "8px" },
          "& a": { color: C.primary },
          "& ul, & ol": { paddingInlineStart: "20px" },
        }} />
    </Box>
  );
};

/* ═══════════════════ BROADCAST PAGE ═══════════════════ */
const BroadcastPage = ({ snack }) => {
  const [tab, setTab] = useState("compose");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sendMode, setSendMode] = useState("all");
  const [showRecipients, setShowRecipients] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [tplDialog, setTplDialog] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const EMPTY_TPL_FORM = { name: "", headerColor: "#4f46e5", headerColor2: "#7c3aed", logoText: "קליקרים", headerSubtitle: "", buttonText: "", buttonUrl: "", footerText: "© 2025 קליקרים • כל הזכויות שמורות", defaultSubject: "", defaultBody: "" };
  const [tplForm, setTplForm] = useState(EMPTY_TPL_FORM);
  const [tplSaving, setTplSaving] = useState(false);
  // History recipients dialog
  const [histRecDialog, setHistRecDialog] = useState(null);
  const [histRecLoading, setHistRecLoading] = useState(false);
  const [histRecData, setHistRecData] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try { const d = await api.get("/broadcast/history"); setHistory(Array.isArray(d.history) ? d.history : []); }
    catch { snack("שגיאה בטעינת היסטוריה", "error"); }
    setHistoryLoading(false);
  }, [snack]);

  const loadRecipients = useCallback(async () => {
    setRecipientsLoading(true);
    try { const d = await api.get("/broadcast/recipients"); setRecipients(Array.isArray(d.recipients) ? d.recipients : []); }
    catch { snack("שגיאה בטעינת לקוחות", "error"); }
    setRecipientsLoading(false);
  }, [snack]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try { const d = await api.get("/broadcast/templates"); setTemplates(Array.isArray(d.templates) ? d.templates : []); }
    catch { snack("שגיאה בטעינת תבניות", "error"); }
    setTemplatesLoading(false);
  }, [snack]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);
  useEffect(() => { if (sendMode === "selected" && recipients.length === 0) loadRecipients(); }, [sendMode, recipients.length, loadRecipients]);

  const filteredRecipients = recipients.filter(r => {
    const q = recipientSearch.toLowerCase();
    return `${r.first_name} ${r.last_name} ${r.email}`.toLowerCase().includes(q);
  });
  const toggleSelect = id => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => {
    const all = filteredRecipients.map(r => r.user_id);
    const allSel = all.every(id => selectedIds.includes(id));
    setSelectedIds(p => allSel ? p.filter(id => !all.includes(id)) : [...new Set([...p, ...all])]);
  };
  const allFilteredSelected = filteredRecipients.length > 0 && filteredRecipients.every(r => selectedIds.includes(r.user_id));

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { snack("יש למלא נושא ותוכן", "warning"); return; }
    if (sendMode === "selected" && selectedIds.length === 0) { snack("יש לבחור לפחות לקוח אחד", "warning"); return; }
    setSending(true); setResult(null);
    try {
      const payload = {
        subject,
        html: body,
        templateId: selectedTemplate,
        ...(sendMode === "selected" ? { userIds: selectedIds } : {}),
      };
      const data = await api.post("/broadcast", payload);
      if (data.status === "success") {
        setResult(data);
        snack(`נשלח בהצלחה ל-${data.sent} נמענים`, "success");
        setSubject(""); setBody(""); setSelectedIds([]);
      } else snack(data.error || "שגיאה בשליחה", "error");
    } catch { snack("שגיאה בשליחה", "error"); }
    setSending(false);
  };

  const loadHistRecipients = async (broadcast) => {
    setHistRecDialog(broadcast);
    setHistRecLoading(true);
    setHistRecData(null);
    try {
      const d = await api.get(`/broadcast/${broadcast.id}/recipients`);
      setHistRecData(d);
    } catch { snack("שגיאה בטעינת נמענים", "error"); }
    setHistRecLoading(false);
  };

  const loadTemplateContent = () => {
    if (!selectedTplData) return;
    if (selectedTplData.defaultSubject) setSubject(selectedTplData.defaultSubject);
    if (selectedTplData.defaultBody) setBody(selectedTplData.defaultBody);
    snack("נטען תוכן התבנית", "success");
  };

  const openCreateTpl = () => {
    setEditingTpl(null);
    setTplForm(EMPTY_TPL_FORM);
    setTplDialog(true);
  };
  const openEditTpl = tpl => {
    setEditingTpl(tpl);
    setTplForm({ name: tpl.name, headerColor: tpl.headerColor || "#4f46e5", headerColor2: tpl.headerColor2 || "#7c3aed", logoText: tpl.logoText || "קליקרים", headerSubtitle: tpl.headerSubtitle || "", buttonText: tpl.buttonText || "", buttonUrl: tpl.buttonUrl || "", footerText: tpl.footerText || "© 2025 קליקרים", defaultSubject: tpl.defaultSubject || "", defaultBody: tpl.defaultBody || "" });
    setTplDialog(true);
  };
  const saveTpl = async () => {
    if (!tplForm.name.trim()) { snack("יש להזין שם תבנית", "warning"); return; }
    setTplSaving(true);
    try {
      if (editingTpl) { await api.put(`/broadcast/templates/${editingTpl.id}`, tplForm); snack("תבנית עודכנה", "success"); }
      else { await api.post("/broadcast/templates", tplForm); snack("תבנית נוצרה", "success"); }
      setTplDialog(false); loadTemplates();
    } catch { snack("שגיאה בשמירה", "error"); }
    setTplSaving(false);
  };
  const deleteTpl = async id => {
    try { await api.del(`/broadcast/templates/${id}`); snack("תבנית נמחקה", "success"); loadTemplates(); }
    catch { snack("שגיאה במחיקה", "error"); }
  };

  const selectedTplData = templates.find(t => t.id === selectedTemplate) || templates[0];

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>
          <CampaignIcon sx={{ verticalAlign: "middle", mr: 1, color: C.primary }} />
          הודעות תפוצה
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {[
            { key: "compose", label: "כתיבה ושליחה", icon: <SendIcon fontSize="small" /> },
            { key: "templates", label: "ניהול תבניות", icon: <PaletteIcon fontSize="small" /> },
            { key: "history", label: "היסטוריה", icon: <HistoryIcon fontSize="small" /> },
          ].map(t => (
            <Button key={t.key} variant={tab === t.key ? "contained" : "outlined"} size="small"
              startIcon={t.icon} onClick={() => setTab(t.key)}
              sx={{ borderRadius: 3, fontWeight: 700,
                ...(tab === t.key ? { background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` } : {}) }}>
              {t.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ── COMPOSE TAB ── */}
      {tab === "compose" && (
        <Grid container spacing={3}>
          {/* Main compose form */}
          <Grid item xs={12} md={7}>
            <StyledCard>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>📢 הודעה חדשה</Typography>

                {/* Send mode toggle */}
                <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
                  {[{ key: "all", label: "כל הלקוחות" }, { key: "selected", label: "לקוחות נבחרים" }].map(m => (
                    <Chip key={m.key}
                      label={m.key === "selected" && selectedIds.length > 0 ? `${m.label} (${selectedIds.length})` : m.label}
                      onClick={() => { setSendMode(m.key); if (m.key === "selected") { setShowRecipients(true); if (recipients.length === 0) loadRecipients(); } }}
                      sx={{ fontWeight: 700, cursor: "pointer",
                        bgcolor: sendMode === m.key ? C.primary : C.bg,
                        color: sendMode === m.key ? "#fff" : C.mid,
                        border: `1px solid ${sendMode === m.key ? C.primary : C.border}` }} />
                  ))}
                </Box>

                <TextField fullWidth label="נושא ההודעה" value={subject}
                  onChange={e => setSubject(e.target.value)}
                  sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />

                {/* Template selector */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontSize: ".8rem", fontWeight: 600, color: C.mid }}>עיצוב המייל</Typography>
                    {selectedTplData && (selectedTplData.defaultSubject || selectedTplData.defaultBody) && (
                      <Button size="small" variant="outlined" onClick={loadTemplateContent}
                        sx={{ borderRadius: 2, fontSize: ".72rem", py: .3 }}>
                        טען תוכן מהתבנית
                      </Button>
                    )}
                  </Box>
                  {templatesLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : (
                    <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 1, scrollbarWidth: "thin" }}>
                      {templates.map(t => (
                        <Box key={t.id} onClick={() => setSelectedTemplate(t.id)}
                          sx={{ minWidth: 88, cursor: "pointer", borderRadius: 2, overflow: "hidden", flexShrink: 0,
                            border: `2px solid ${selectedTemplate === t.id ? (t.headerColor || C.primary) : C.border}`,
                            boxShadow: selectedTemplate === t.id ? `0 0 0 3px ${(t.headerColor || C.primary)}33` : "none",
                            transition: "all .2s" }}>
                          <Box sx={{ height: 30, background: `linear-gradient(135deg, ${t.headerColor || C.primary}, ${t.headerColor2 || "#7c3aed"})` }} />
                          <Box sx={{ px: 1, py: .6, background: "#fff" }}>
                            <Typography sx={{ fontSize: ".67rem", fontWeight: 700, color: C.dark, textAlign: "center",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {t.name}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Rich text editor */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: ".8rem", fontWeight: 600, color: C.mid, mb: .8 }}>תוכן ההודעה</Typography>
                  <RichTextEditor value={body} onChange={setBody} minHeight={220} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button variant="contained"
                    startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    onClick={handleSend} disabled={sending}
                    sx={{ borderRadius: 3, fontWeight: 700, px: 4, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
                    {sending ? "שולח..." : sendMode === "all" ? "שלח לכולם" : `שלח ל-${selectedIds.length} נבחרים`}
                  </Button>
                  {result && (
                    <Typography variant="body2" sx={{ color: C.green, fontWeight: 700 }}>
                      ✅ נשלח ל-{result.sent} | נכשל: {result.failed}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, background: C.amberBg, border: `1px solid ${C.amber}33` }}>
                  <Typography variant="caption" sx={{ color: C.amber, fontWeight: 600 }}>
                    ⚠ קישור הסרה מרשימת התפוצה יתווסף אוטומטית לכל הודעה
                  </Typography>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={5}>
            <StyledCard sx={{ background: "#eef2ff", position: "sticky", top: 16 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 700, color: C.light, mb: 1.5, fontSize: ".82rem" }}>תצוגה מקדימה</Typography>
                <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", background: "#fff", minHeight: 260 }}>
                  <Box sx={{ height: 50, background: `linear-gradient(135deg, ${selectedTplData?.headerColor || C.primary}, ${selectedTplData?.headerColor2 || "#7c3aed"})`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box sx={{ bgcolor: "rgba(255,255,255,.18)", borderRadius: 1.5, px: 2, py: .4 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".88rem", letterSpacing: .5 }}>
                        {selectedTplData?.logoText || "קליקרים"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: 2.5, py: 1.5, background: `${selectedTplData?.headerColor || C.primary}12`, borderBottom: `2px solid ${selectedTplData?.headerColor || C.primary}22` }}>
                    <Typography sx={{ fontWeight: 800, color: selectedTplData?.headerColor || C.primary, fontSize: ".88rem" }}>
                      {subject || <span style={{ color: C.light, fontWeight: 400 }}>נושא ההודעה...</span>}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2.5, py: 2, maxHeight: 300, overflowY: "auto" }}
                    dangerouslySetInnerHTML={{ __html: body || `<span style="color:${C.light}">תוכן ההודעה יופיע כאן...</span>` }} />
                  <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
                    <Typography variant="caption" sx={{ color: "#9ca3af", fontSize: ".72rem" }}>
                      {selectedTplData?.footerText || "© 2025 קליקרים"}{" • "}
                      <span style={{ color: "#a78bfa", textDecoration: "underline" }}>הסר אותי מרשימת התפוצה</span>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Recipient selector — full width, below */}
          {sendMode === "selected" && (
            <Grid item xs={12}>
              <StyledCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, cursor: "pointer" }}
                    onClick={() => setShowRecipients(p => !p)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <GroupAddIcon sx={{ color: C.primary }} />
                      <Typography sx={{ fontWeight: 700, color: C.dark }}>בחירת לקוחות</Typography>
                      <Chip label={`${selectedIds.length} נבחרו`} size="small" sx={{ fontWeight: 700, bgcolor: C.primaryLight, color: C.primary }} />
                    </Box>
                    {showRecipients ? <ExpandLessIcon sx={{ color: C.mid }} /> : <ExpandMoreIcon sx={{ color: C.mid }} />}
                  </Box>
                  <Collapse in={showRecipients}>
                    <TextField size="small" fullWidth placeholder="חיפוש לקוח..."
                      value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: C.light }} /></InputAdornment> }}
                      sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    {recipientsLoading ? <LinearProgress /> : (
                      <>
                        <Box onClick={toggleAll}
                          sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: .8, mb: .5,
                            borderRadius: 2, cursor: "pointer", bgcolor: C.bg, "&:hover": { bgcolor: C.primaryLight } }}>
                          <Checkbox size="small" checked={allFilteredSelected}
                            indeterminate={filteredRecipients.some(r => selectedIds.includes(r.user_id)) && !allFilteredSelected}
                            sx={{ p: 0 }} />
                          <Typography sx={{ fontSize: ".82rem", fontWeight: 700, color: C.dark }}>
                            בחר הכל ({filteredRecipients.length})
                          </Typography>
                        </Box>
                        <Grid container spacing={1} sx={{ maxHeight: 300, overflowY: "auto", pr: .5 }}>
                          {filteredRecipients.map(r => {
                            const checked = selectedIds.includes(r.user_id);
                            const hasEmail = r.email && r.email.includes("@");
                            return (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={r.user_id}>
                                <Box onClick={() => hasEmail && toggleSelect(r.user_id)}
                                  sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1,
                                    borderRadius: 2, cursor: hasEmail ? "pointer" : "default",
                                    opacity: hasEmail ? 1 : .45, bgcolor: checked ? C.primaryLight : C.bg,
                                    border: `1px solid ${checked ? C.primary + "44" : C.border}`,
                                    "&:hover": hasEmail ? { bgcolor: checked ? C.primaryLight : "#f1f5f9" } : {} }}>
                                  <Checkbox size="small" checked={checked} disabled={!hasEmail} sx={{ p: 0 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: ".82rem", fontWeight: checked ? 700 : 500, color: C.dark,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {r.first_name} {r.last_name}
                                    </Typography>
                                    <Typography sx={{ fontSize: ".7rem", color: hasEmail ? C.light : C.red,
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {hasEmail ? r.email : "אין מייל"}
                                    </Typography>
                                  </Box>
                                  {r.unsubscribed && <Chip label="הוסר" size="small" sx={{ fontSize: ".6rem", height: 16, bgcolor: C.redBg, color: C.red }} />}
                                </Box>
                              </Grid>
                            );
                          })}
                          {filteredRecipients.length === 0 && (
                            <Grid item xs={12}><Typography sx={{ color: C.light, textAlign: "center", py: 3, fontSize: ".85rem" }}>לא נמצאו לקוחות</Typography></Grid>
                          )}
                        </Grid>
                      </>
                    )}
                  </Collapse>
                </CardContent>
              </StyledCard>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── TEMPLATES TAB ── */}
      {tab === "templates" && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: C.mid }}>
              {templates.length} תבניות זמינות ({templates.filter(t => !t.isBuiltIn).length} מותאמות אישית)
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateTpl}
              sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
              תבנית חדשה
            </Button>
          </Box>
          {templatesLoading ? <LinearProgress /> : (
            <Grid container spacing={2.5}>
              {templates.map(t => (
                <Grid item xs={12} sm={6} md={4} key={t.id}>
                  <StyledCard sx={{ cursor: "pointer", position: "relative",
                    border: `2px solid ${selectedTemplate === t.id ? (t.headerColor || C.primary) : C.border}` }}
                    onClick={() => { setSelectedTemplate(t.id); setTab("compose"); }}>
                    {/* Color preview */}
                    <Box sx={{ height: 84, background: `linear-gradient(135deg, ${t.headerColor || C.primary}, ${t.headerColor2 || "#7c3aed"})`,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: .5 }}>
                      <Box sx={{ bgcolor: "rgba(255,255,255,.18)", borderRadius: 2, px: 2, py: .5 }}>
                        <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1rem", letterSpacing: 1 }}>
                          {t.logoText || "קליקרים"}
                        </Typography>
                      </Box>
                      {t.headerSubtitle && (
                        <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: ".7rem" }}>{t.headerSubtitle}</Typography>
                      )}
                    </Box>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: C.dark }}>{t.name}</Typography>
                          {t.isBuiltIn && (
                            <Chip label="מובנה" size="small"
                              sx={{ mt: .5, fontSize: ".65rem", height: 18, bgcolor: C.primaryLight, color: C.primary, fontWeight: 700 }} />
                          )}
                        </Box>
                        {!t.isBuiltIn && (
                          <Box sx={{ display: "flex", gap: .5 }} onClick={e => e.stopPropagation()}>
                            <IconButton size="small" onClick={() => openEditTpl(t)} sx={{ color: C.primary }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => deleteTpl(t.id)} sx={{ color: C.red }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {selectedTemplate === t.id && (
                        <Chip label="נבחרה" size="small"
                          sx={{ mt: 1, fontWeight: 700, bgcolor: t.headerColor || C.primary, color: "#fff", fontSize: ".68rem" }} />
                      )}
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === "history" && (
        historyLoading ? <LinearProgress /> : history.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CampaignIcon sx={{ fontSize: 48, color: C.light, mb: 1 }} />
            <Typography sx={{ color: C.light, fontWeight: 600 }}>אין היסטוריית שליחות</Typography>
          </Box>
        ) : (
          <StyledCard>
            <Table>
              <TableHead sx={{ background: C.bg }}>
                <TableRow>
                  {["נושא", "תבנית", "תאריך", "נמענים", "נשלח", "נכשל", ""].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: 700, color: C.dark }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{h.subject}</TableCell>
                    <TableCell sx={{ fontSize: ".82rem", color: C.mid }}>{h.templateName || "—"}</TableCell>
                    <TableCell sx={{ fontSize: ".82rem", color: C.light }}>{new Date(h.sentAt).toLocaleString("he-IL")}</TableCell>
                    <TableCell><Chip label={h.targeted || "כולם"} size="small" sx={{ bgcolor: C.primaryLight, color: C.primary, fontWeight: 700, fontSize: ".7rem" }} /></TableCell>
                    <TableCell><Chip label={h.sent} size="small" sx={{ bgcolor: C.greenBg, color: C.green, fontWeight: 700 }} /></TableCell>
                    <TableCell><Chip label={h.failed} size="small" sx={{ bgcolor: h.failed > 0 ? C.redBg : C.bg, color: h.failed > 0 ? C.red : C.light, fontWeight: 700 }} /></TableCell>
                    <TableCell>
                      {h.id && (
                        <Button size="small" startIcon={<GroupAddIcon />} onClick={() => loadHistRecipients(h)}
                          sx={{ borderRadius: 2, fontSize: ".72rem", color: C.primary }}>
                          נמענים
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledCard>
        )
      )}

      {/* Template create/edit dialog */}
      <Dialog open={tplDialog} onClose={() => setTplDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: C.dark }}>
          {editingTpl ? "עריכת תבנית" : "תבנית חדשה"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="שם התבנית" value={tplForm.name}
                onChange={e => setTplForm(p => ({ ...p, name: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: ".8rem", fontWeight: 600, color: C.mid, mb: 1 }}>צבע ראשי</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box component="input" type="color" value={tplForm.headerColor}
                  onChange={e => setTplForm(p => ({ ...p, headerColor: e.target.value }))}
                  style={{ width: 44, height: 38, border: "none", padding: 2, borderRadius: 8, cursor: "pointer" }} />
                <TextField size="small" value={tplForm.headerColor}
                  onChange={e => setTplForm(p => ({ ...p, headerColor: e.target.value }))}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: ".8rem", fontWeight: 600, color: C.mid, mb: 1 }}>צבע משני (גרדיאנט)</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box component="input" type="color" value={tplForm.headerColor2}
                  onChange={e => setTplForm(p => ({ ...p, headerColor2: e.target.value }))}
                  style={{ width: 44, height: 38, border: "none", padding: 2, borderRadius: 8, cursor: "pointer" }} />
                <TextField size="small" value={tplForm.headerColor2}
                  onChange={e => setTplForm(p => ({ ...p, headerColor2: e.target.value }))}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Box>
            </Grid>
            {/* Live color preview */}
            <Grid item xs={12}>
              <Box sx={{ height: 60, borderRadius: 2.5, overflow: "hidden",
                background: `linear-gradient(135deg, ${tplForm.headerColor}, ${tplForm.headerColor2})`,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{ bgcolor: "rgba(255,255,255,.18)", borderRadius: 1.5, px: 2.5, py: .6 }}>
                  <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.05rem" }}>
                    {tplForm.logoText || "קליקרים"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="שם הלוגו / מותג" value={tplForm.logoText}
                onChange={e => setTplForm(p => ({ ...p, logoText: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="כותרת משנה בהדר" value={tplForm.headerSubtitle}
                onChange={e => setTplForm(p => ({ ...p, headerSubtitle: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="טקסט כפתור (אופציונלי)" value={tplForm.buttonText}
                onChange={e => setTplForm(p => ({ ...p, buttonText: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="קישור הכפתור" value={tplForm.buttonUrl}
                onChange={e => setTplForm(p => ({ ...p, buttonUrl: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="טקסט פוטר" value={tplForm.footerText}
                onChange={e => setTplForm(p => ({ ...p, footerText: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: .5 }} />
              <Typography sx={{ fontSize: ".8rem", fontWeight: 700, color: C.mid, mb: 1 }}>תוכן ברירת מחדל (אופציונלי)</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="נושא ברירת מחדל" value={tplForm.defaultSubject}
                onChange={e => setTplForm(p => ({ ...p, defaultSubject: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: ".76rem", color: C.mid, mb: .8 }}>תוכן ברירת מחדל</Typography>
              <RichTextEditor value={tplForm.defaultBody} onChange={val => setTplForm(p => ({ ...p, defaultBody: val }))} minHeight={120} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setTplDialog(false)} sx={{ borderRadius: 3 }}>ביטול</Button>
          <Button variant="contained" onClick={saveTpl} disabled={tplSaving}
            startIcon={tplSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            {tplSaving ? "שומר..." : "שמור תבנית"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History recipients dialog */}
      <Dialog open={!!histRecDialog} onClose={() => setHistRecDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: C.dark, pb: 1 }}>
          נמענים — {histRecDialog?.subject}
          <Typography variant="body2" sx={{ color: C.light, fontWeight: 400, mt: .5 }}>
            {histRecDialog && new Date(histRecDialog.sentAt).toLocaleString("he-IL")} • {histRecDialog?.total} נמענים
          </Typography>
        </DialogTitle>
        <DialogContent>
          {histRecLoading ? <LinearProgress /> : histRecData ? (
            histRecData.recipients.length === 0 ? (
              <Typography sx={{ color: C.light, textAlign: "center", py: 3 }}>אין נתוני נמענים לשליחה זו</Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                {histRecData.recipients.map((r, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1, py: .8,
                    borderRadius: 2, "&:hover": { bgcolor: C.bg } }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: ".75rem", bgcolor: C.primaryLight, color: C.primary }}>
                      {typeof r === "string" && r.includes("@") ? r[0].toUpperCase() : (i + 1)}
                    </Avatar>
                    <Typography sx={{ fontSize: ".85rem", color: C.dark }}>{r}</Typography>
                  </Box>
                ))}
              </Box>
            )
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setHistRecDialog(null)} sx={{ borderRadius: 3 }}>סגור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ MAILBOXES PAGE ═══════════════════ */
const MailboxesPage = ({ snack }) => {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [newMail, setNewMail] = useState({ username: '', password: '' });
  const [aliasDialog, setAliasDialog] = useState(false);
  const [aliasData, setAliasData] = useState({ alias: '', target: '' });
  const [pwDialog, setPwDialog] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchMailboxes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/mailboxes');
      setMailboxes(Array.isArray(data) ? data : []);
    } catch {
      snack('שגיאה בטעינה', 'error');
    }
    setLoading(false);
  }, [snack]);

  useEffect(() => { fetchMailboxes(); }, [fetchMailboxes]);

  const handleCreate = async () => {
    if (!newMail.username || !newMail.password) return snack('נא למלא את כל השדות', 'warning');
    setSaving(true);
    try {
      const emailAddr = newMail.username.includes('@') ? newMail.username : `${newMail.username}@clickerim.co.il`;
      const res = await api.post('/mailboxes', { email: emailAddr, password: newMail.password });
      if (res.status === 'success') {
        snack(`תיבה ${emailAddr} נוצרה בהצלחה`, 'success');
        setAddDialog(false);
        setNewMail({ username: '', password: '' });
        fetchMailboxes();
      } else {
        snack(res.error || 'שגיאה', 'error');
      }
    } catch { snack('שגיאה', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (emailAddr) => {
    if (!window.confirm(`למחוק את התיבה ${emailAddr}?`)) return;
    try {
      await api.del(`/mailboxes/${encodeURIComponent(emailAddr)}`);
      snack('תיבה נמחקה', 'success');
      fetchMailboxes();
    } catch { snack('שגיאה', 'error'); }
  };

  const handleChangePassword = async () => {
    if (!newPw || newPw.length < 4) return snack('סיסמה קצרה מדי', 'warning');
    setSaving(true);
    try {
      const res = await api.post(`/mailboxes/${encodeURIComponent(pwDialog)}/password`, { password: newPw });
      if (res.status === 'success') {
        snack('סיסמה שונתה', 'success');
        setPwDialog(null);
        setNewPw('');
      } else {
        snack(res.error || 'שגיאה', 'error');
      }
    } catch { snack('שגיאה', 'error'); }
    setSaving(false);
  };

  const handleCreateAlias = async () => {
    if (!aliasData.alias || !aliasData.target) return snack('נא למלא את כל השדות', 'warning');
    setSaving(true);
    try {
      const alias = aliasData.alias.includes('@') ? aliasData.alias : `${aliasData.alias}@clickerim.co.il`;
      const target = aliasData.target.includes('@') ? aliasData.target : `${aliasData.target}@clickerim.co.il`;
      const res = await api.post('/mailboxes/alias', { alias, target });
      if (res.status === 'success') {
        snack(`אליאס ${alias} → ${target} נוצר`, 'success');
        setAliasDialog(false);
        setAliasData({ alias: '', target: '' });
        fetchMailboxes();
      } else {
        snack(res.error || 'שגיאה', 'error');
      }
    } catch { snack('שגיאה', 'error'); }
    setSaving(false);
  };

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>📧 ניהול תיבות מייל</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialog(true)}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            תיבה חדשה
          </Button>
          <Button variant="outlined" onClick={() => setAliasDialog(true)}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            ➕ אליאס
          </Button>
          <IconButton onClick={fetchMailboxes}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Info */}
      <Box sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: C.blueBg, border: `1px solid ${C.blue}22` }}>
        <Typography sx={{ fontSize: '.82rem', color: C.blue, fontWeight: 600 }}>
          ℹ️ ניהול תיבות דוא"ל בדומיין clickerim.co.il — יצירה, מחיקה, שינוי סיסמה ואליאסים
        </Typography>
      </Box>

      {/* Mailboxes list */}
      {mailboxes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontSize: '3rem', mb: 1 }}>📭</Typography>
          <Typography sx={{ color: C.light, fontWeight: 600 }}>אין תיבות מייל</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {mailboxes.map((mb, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <StyledCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: '12px',
                      background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <AlternateEmailIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: C.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {mb.email}
                      </Typography>
                      <Typography sx={{ fontSize: '.7rem', color: C.light }}>
                        {mb.raw && mb.raw !== mb.email ? mb.raw : 'תיבה פעילה'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: .5 }}>
                    <Tooltip title="שינוי סיסמה">
                      <IconButton size="small" onClick={() => setPwDialog(mb.email)}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="מחק">
                      <IconButton size="small" onClick={() => handleDelete(mb.email)} sx={{ color: C.red }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Mailbox Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>📧 יצירת תיבת מייל חדשה</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="שם משתמש" value={newMail.username}
                onChange={e => setNewMail({ ...newMail, username: e.target.value })}
                helperText={newMail.username ? `${newMail.username}@clickerim.co.il` : ''}
                placeholder="לדוגמה: support" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="סיסמה" type="password" value={newMail.password}
                onChange={e => setNewMail({ ...newMail, password: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            {saving ? <CircularProgress size={20} /> : 'צור תיבה'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alias Dialog */}
      <Dialog open={aliasDialog} onClose={() => setAliasDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>↗️ יצירת אליאס (הפניה)</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '.8rem', color: C.light, mb: 2 }}>
            מיילים שנשלחים לאליאס יועברו אוטומטית לתיבה היעד
          </Typography>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="אליאס (כתובת מקור)" value={aliasData.alias}
                onChange={e => setAliasData({ ...aliasData, alias: e.target.value })}
                placeholder="support@clickerim.co.il" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="תיבה יעד (לאן להפנות)" value={aliasData.target}
                onChange={e => setAliasData({ ...aliasData, target: e.target.value })}
                placeholder="cs@clickerim.co.il" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAliasDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleCreateAlias} disabled={saving}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : 'צור אליאס'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!pwDialog} onClose={() => { setPwDialog(null); setNewPw(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🔑 שינוי סיסמה ל-{pwDialog}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="סיסמה חדשה" type="password" value={newPw}
            onChange={e => setNewPw(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setPwDialog(null); setNewPw(''); }}>ביטול</Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={saving}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ STATS PAGE (EXTENDED) ═══════════════════ */
const StatsPage = ({ stats, loading }) => {
  if (loading) return <LinearProgress />;
  if (!stats) return null;
  const o = stats.overview;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark, mb: 3 }}>📊 סטטיסטיקות מפורטות</Typography>

      {/* Registration chart */}
      <StyledCard sx={{ mb: 4 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2, fontSize: "1.1rem" }}>📈 גרף הרשמות — 30 יום אחרונים</Typography>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4px", height: 140 }}>
            {stats.registrationChart?.map((d, i) => {
              const maxVal = Math.max(...stats.registrationChart.map(x => x.count), 1);
              const h = (d.count / maxVal) * 120 + 6;
              return (
                <Tooltip key={i} title={`${d.date}: ${d.count} הרשמות`} arrow>
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Typography sx={{ fontSize: ".55rem", color: C.light, mb: .3 }}>{d.count > 0 ? d.count : ""}</Typography>
                    <Box sx={{
                      width: "100%", height: h, borderRadius: "5px 5px 0 0",
                      background: d.count > 0 ? `linear-gradient(180deg, ${C.primary}, #818cf8)` : C.border,
                      transition: "height .4s",
                    }} />
                    {i % 5 === 0 && <Typography sx={{ fontSize: ".5rem", color: C.light, mt: .3 }}>{d.date}</Typography>}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </CardContent>
      </StyledCard>

      {/* New clients breakdown */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>👤 לקוחות חדשים</Typography>
              {[
                { label: "היום", val: stats.newClients?.today, color: C.green, icon: "🟢" },
                { label: "השבוע", val: stats.newClients?.week, color: C.blue, icon: "🔵" },
                { label: "החודש", val: stats.newClients?.month, color: C.purple, icon: "🟣" },
              ].map((x, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, borderBottom: i < 2 ? `1px solid ${C.border}` : "none" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>{x.icon}</Typography>
                    <Typography sx={{ fontWeight: 600, color: C.dark }}>{x.label}</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", color: x.color }}>{x.val}</Typography>
                </Box>
              ))}
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>📊 התפלגות שאלות</Typography>
              {stats.questionsByType?.map((qt, i) => {
                const typeNames = { q: "שאלה רגילה", img: "תמונה", video: "וידאו", audio: "שמע" };
                const colors = [C.primary, C.pink, C.amber, C.green];
                const total = stats.questionsByType.reduce((s, q) => s + q.count, 0) || 1;
                const pct = Math.round((qt.count / total) * 100);
                return (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: .5 }}>
                      <Typography sx={{ fontSize: ".85rem", color: C.dark, fontWeight: 600 }}>{typeNames[qt.type] || qt.type}</Typography>
                      <Typography sx={{ fontSize: ".85rem", color: C.light }}>{qt.count} ({pct}%)</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct}
                      sx={{ height: 8, borderRadius: 4, background: C.border, "& .MuiLinearProgress-bar": { borderRadius: 4, background: colors[i % colors.length] } }} />
                  </Box>
                );
              })}
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>📞 ניצולת מספרים</Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress variant="determinate" value={o.totalPhones ? (o.busyPhones / o.totalPhones) * 100 : 0}
                    size={120} thickness={6}
                    sx={{ color: C.primary, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }} />
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: C.dark }}>
                      {o.totalPhones ? Math.round((o.busyPhones / o.totalPhones) * 100) : 0}%
                    </Typography>
                    <Typography sx={{ fontSize: ".65rem", color: C.light }}>ניצולת</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 700, color: C.green }}>{o.freePhones}</Typography>
                  <Typography sx={{ fontSize: ".7rem", color: C.light }}>פנויים</Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 700, color: C.red }}>{o.busyPhones}</Typography>
                  <Typography sx={{ fontSize: ".7rem", color: C.light }}>תפוסים</Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontWeight: 700, color: C.primary }}>{o.totalPhones}</Typography>
                  <Typography sx={{ fontSize: ".7rem", color: C.light }}>סה״כ</Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Top tables */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>🏆 טופ 10 לקוחות</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>שם</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>טלפון</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>משחקים</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topClients?.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Avatar sx={{ width: 24, height: 24, fontSize: ".7rem", fontWeight: 700, background: i < 3 ? [C.amber, C.light, "#cd7f32"][i] : C.border }}>{i + 1}</Avatar>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: ".85rem" }}>{c.name}</TableCell>
                        <TableCell sx={{ fontSize: ".82rem", color: C.mid }}>{c.phone}</TableCell>
                        <TableCell><Chip label={c.gamesCount} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardContent>
              <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2 }}>🎮 משחקים פופולריים</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>משחק</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>בעלים</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>שחקנים</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topGames?.map((g, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Avatar sx={{ width: 24, height: 24, fontSize: ".7rem", fontWeight: 700, background: i < 3 ? [C.amber, C.light, "#cd7f32"][i] : C.border }}>{i + 1}</Avatar>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: ".85rem" }}>{g.name}</TableCell>
                        <TableCell sx={{ fontSize: ".82rem", color: C.mid }}>{g.owner}</TableCell>
                        <TableCell><Chip label={g.playersCount} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Agent Performance */}
      {stats.agentStats?.length > 0 && (
        <StyledCard sx={{ mt: 4 }}>
          <CardContent>
            <Typography sx={{ fontWeight: 700, color: C.dark, mb: 2, fontSize: "1.1rem" }}>🧑‍💼 ביצועי נציגים</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: C.bg }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>נציג</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>פניות משויכות</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>פתוחות</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>סגורות</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>תגובות</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: ".78rem" }}>זמן מענה ממוצע</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.agentStats.map((a, i) => (
                    <TableRow key={i} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: ".75rem", fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
                            {a.name?.[0] || "?"}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600, fontSize: ".85rem" }}>{a.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={a.totalAssigned} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                      <TableCell><Chip label={a.openAssigned} size="small" sx={{ fontWeight: 700, background: C.amberBg, color: C.amber }} /></TableCell>
                      <TableCell><Chip label={a.closedCount} size="small" sx={{ fontWeight: 700, background: C.greenBg, color: C.green }} /></TableCell>
                      <TableCell><Chip label={a.totalReplies} size="small" variant="outlined" sx={{ fontWeight: 700 }} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: .5 }}>
                          <TimerIcon sx={{ fontSize: 16, color: C.light }} />
                          <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: a.avgResponseMin !== "—" ? C.primary : C.light }}>
                            {a.avgResponseMin !== "—" ? `${a.avgResponseMin} דק׳` : "—"}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      )}
    </Box>
  );
};

/* ═══════════════════ GAMES PAGE ═══════════════════ */
const GamesPage = ({ games, loading, onRefresh }) => {
  const [search, setSearch] = useState("");

  const filtered = games.filter(g =>
    `${g.name} ${g.id} ${g.ownerName} ${g.start || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <LinearProgress />;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>🎮 כל המשחקים ({filtered.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField size="small" placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
            sx={{ width: 250, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
          <IconButton onClick={onRefresh}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: C.bg }}>
              <TableCell sx={{ fontWeight: 700 }}>שם</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>בעלים</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>טלפון בעלים</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>מספר טלפון</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>שחקנים</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>שאלות</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>פתוח</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(game => (
              <TableRow key={game.id} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                <TableCell sx={{ fontWeight: 700 }}>{game.name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: ".8rem", color: C.light }}>{game.id}</TableCell>
                <TableCell>{game.ownerName}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: ".85rem" }}>{game.ownerPhone}</TableCell>
                <TableCell>
                  {game.start ? (
                    <Chip label={game.start} size="small" sx={{ fontWeight: 700, background: C.greenBg, color: C.green, border: `1px solid ${C.green}33` }} />
                  ) : (
                    <Typography sx={{ fontSize: ".8rem", color: C.light }}>—</Typography>
                  )}
                </TableCell>
                <TableCell><Chip label={game.playersCount} size="small" /></TableCell>
                <TableCell><Chip label={game.questionsCount} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <FiberManualRecordIcon sx={{ fontSize: 12, color: game.isOpen ? C.green : C.light }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ═══════════════════ CLIENT DETAIL DIALOG ═══════════════════ */
const ClientDetailPage = ({ clientId, onBack, snack, onRefresh }) => {
  const [detailTab, setDetailTab] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [grantDialog, setGrantDialog] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [availablePackages, setAvailablePackages] = useState([]);
  const [editDialog, setEditDialog] = useState(null);
  const [pwDialog, setPwDialog] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [deletePackageDialog, setDeletePackageDialog] = useState(false);
  const [deletingPackage, setDeletingPackage] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!clientId) return;
    setLoadingDetail(true);
    try {
      const [data, pkgs] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get("/packages"),
      ]);
      setDetail(data);
      setAvailablePackages(pkgs || []);
    } catch {}
    setLoadingDetail(false);
  }, [clientId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleGrantPackage = async () => {
    if (!selectedPackageId) return;
    const body = { user_id: clientId, packageId: selectedPackageId };
    const res = await api.post("/grant-package", body);
    if (res.status === "success") {
      snack("חבילה הוענקה בהצלחה", "success");
      setGrantDialog(false);
      setSelectedPackageId("");
      fetchDetail();
      if (onRefresh) onRefresh();
    } else {
      snack(res.message || "שגיאה", "error");
    }
  };

  const handleSaveEdit = async () => {
    if (!editDialog) return;
    const res = await api.put(`/clients/${clientId}`, editDialog);
    if (res.status === "success") {
      snack("לקוח עודכן בהצלחה", "success");
      setEditDialog(null);
      fetchDetail();
      if (onRefresh) onRefresh();
    } else {
      snack("שגיאה בעדכון", "error");
    }
  };

  const handleResetPw = async () => {
    if (!newPw) return;
    const res = await api.post(`/clients/${clientId}/reset-password`, { newPassword: newPw });
    if (res.status === "success") {
      snack("סיסמה שונתה בהצלחה", "success");
      setPwDialog(false);
      setNewPw("");
    } else {
      snack(res.error || "שגיאה", "error");
    }
  };

  const handleDeletePackage = async () => {
    setDeletingPackage(true);
    try {
      const res = await api.post("/delete-user-package", { user_id: clientId });
      if (res.status === "success") {
        snack(res.message || "החבילה נמחקה בהצלחה", "success");
        setDeletePackageDialog(false);
        fetchDetail();
        if (onRefresh) onRefresh();
      } else {
        snack(res.message || "שגיאה במחיקה", "error");
      }
    } catch {
      snack("שגיאת תקשורת", "error");
    }
    setDeletingPackage(false);
  };

  const handleReplacePackage = () => {
    setDeletePackageDialog(false);
    setGrantDialog(true);
  };

  if (!clientId) return null;

  const client = detail || {};
  const quota = detail?.quota || {};
  const gamesAllowed = quota.gamesAllowed || 0;
  const isUnlimited = gamesAllowed === -1;
  const activatedGames = detail?.activatedGames || 0;
  const storageMB = quota.storageMB || 500;
  const storageUsedMB = Math.round((detail?.storageUsedBytes || 0) / 1024 / 1024);

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      {/* Header with back button */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={onBack} sx={{ background: C.bg, "&:hover": { background: C.border } }}>
          <ArrowBackIcon />
        </IconButton>
        {loadingDetail ? (
          <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>טוען...</Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
            <Avatar sx={{ width: 56, height: 56, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`, fontWeight: 700, fontSize: "1.3rem" }}>
              {client.first_name?.[0]}{client.last_name?.[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>
                {client.first_name} {client.last_name}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                {client.phone && <Chip icon={<PhoneAndroidIcon sx={{ fontSize: 14 }} />} label={client.phone} size="small" variant="outlined" />}
                {client.email && <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={client.email} size="small" variant="outlined" />}
                <Typography sx={{ fontSize: ".72rem", color: C.light }}>{client.createdAt ? `הצטרף ${client.createdAt}` : ""}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: .8 }}>
              <Tooltip title="ערוך פרטים">
                <IconButton onClick={() => setEditDialog({ first_name: client.first_name, last_name: client.last_name, phone: client.phone, email: client.email, notes: client.notes })} sx={{ color: C.primary }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="שנה סיסמה">
                <IconButton onClick={() => setPwDialog(true)} sx={{ color: C.amber }}>
                  <LockResetIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="העתק ID">
                <IconButton onClick={() => { navigator.clipboard.writeText(clientId); snack("ID הועתק", "info"); }} sx={{ color: C.light }}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
      </Box>

      {loadingDetail ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      ) : detail ? (
        <>
          {/* Summary cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <StyledCard>
                <CardContent sx={{ p: 2.5, textAlign: "center", "&:last-child": { pb: 2.5 } }}>
                  <SportsEsportsIcon sx={{ color: C.primary, mb: .5, fontSize: 28 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: C.primary }}>{detail.gamesCount || 0}</Typography>
                  <Typography sx={{ fontSize: ".78rem", color: C.mid }}>משחקים</Typography>
                </CardContent>
              </StyledCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StyledCard>
                <CardContent sx={{ p: 2.5, textAlign: "center", "&:last-child": { pb: 2.5 } }}>
                  <CheckCircleIcon sx={{ color: C.green, mb: .5, fontSize: 28 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: C.green }}>
                    {isUnlimited ? "♾️" : `${activatedGames} / ${gamesAllowed}`}
                  </Typography>
                  <Typography sx={{ fontSize: ".78rem", color: C.mid }}>{isUnlimited ? "ללא הגבלה" : "הפעלות"}</Typography>
                </CardContent>
              </StyledCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StyledCard>
                <CardContent sx={{ p: 2.5, textAlign: "center", "&:last-child": { pb: 2.5 } }}>
                  <GroupsIcon sx={{ color: C.blue, mb: .5, fontSize: 28 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: C.blue }}>{detail.totalPlayers || 0}</Typography>
                  <Typography sx={{ fontSize: ".78rem", color: C.mid }}>שחקנים</Typography>
                </CardContent>
              </StyledCard>
            </Grid>
            <Grid item xs={6} sm={3}>
              <StyledCard>
                <CardContent sx={{ p: 2.5, textAlign: "center", "&:last-child": { pb: 2.5 } }}>
                  <StorageIcon sx={{ color: C.amber, mb: .5, fontSize: 28 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: C.amber }}>{storageUsedMB} / {storageMB}</Typography>
                  <Typography sx={{ fontSize: ".78rem", color: C.mid }}>MB אחסון</Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} variant="fullWidth"
            sx={{ mb: 3, borderRadius: 3, background: C.white, border: `1px solid ${C.border}`, "& .MuiTab-root": { fontWeight: 700, fontSize: ".9rem" } }}>
            <Tab label="🎮 משחקים" />
            <Tab label="📦 חבילות ורכישות" />
            <Tab label="📋 היסטוריה" />
          </Tabs>

          {/* Tab 0 — Games */}
          {detailTab === 0 && (
            <StyledCard>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: C.bg }}>
                      <TableCell sx={{ fontWeight: 700 }}>שם המשחק</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>מספר טלפון</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>שחקנים</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>שאלות</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>הופעל</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.games?.length > 0 ? detail.games.map((g, i) => (
                      <TableRow key={i} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {g.start ? (
                            <Chip label={g.start} size="small" sx={{ fontWeight: 700, background: C.greenBg, color: C.green }} />
                          ) : "—"}
                        </TableCell>
                        <TableCell>{g.playersCount || 0}</TableCell>
                        <TableCell>{g.questionsCount || 0}</TableCell>
                        <TableCell sx={{ fontSize: ".82rem", color: C.mid }}>{g.lastStart || "לא הופעל"}</TableCell>
                        <TableCell>
                          <Chip
                            label={g.isOpen ? "פתוח" : "סגור"}
                            size="small"
                            sx={{ fontWeight: 600, background: g.isOpen ? C.greenBg : C.border, color: g.isOpen ? C.green : C.light }}
                          />
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", color: C.light, py: 4 }}>אין משחקים</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledCard>
          )}

          {/* Tab 1 — Packages & Purchases */}
          {detailTab === 1 && (
            <Box>
              {/* Current quota */}
              <StyledCard sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: C.dark }}>📦 מצב חבילה נוכחי</Typography>
                      {detail?.currentPackageName ? (
                        <Chip label={detail.currentPackageName} sx={{ fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}18, ${C.purple}18)`, color: C.primary, fontSize: ".85rem", height: 32 }} />
                      ) : (
                        gamesAllowed === 0 && <Chip label="אין חבילה" sx={{ fontWeight: 600, background: C.redBg, color: C.red, fontSize: ".82rem" }} />
                      )}
                      {detail?.currentPackageDate && (
                        <Typography sx={{ fontSize: ".72rem", color: C.light }}>מתאריך {detail.currentPackageDate}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {(gamesAllowed > 0 || isUnlimited) && (
                        <Button variant="outlined" color="error" size="small"
                          onClick={() => setDeletePackageDialog(true)}
                          startIcon={<DeleteIcon />}
                          sx={{ borderRadius: 3, fontWeight: 600, fontSize: ".8rem" }}>
                          מחק חבילה
                        </Button>
                      )}
                      <Button variant="outlined" size="small"
                        onClick={() => setGrantDialog(true)}
                        startIcon={<AutorenewIcon />}
                        sx={{ borderRadius: 3, fontWeight: 600, fontSize: ".8rem", borderColor: C.primary, color: C.primary }}>
                        {gamesAllowed > 0 || isUnlimited ? "החלף חבילה" : "הענק חבילה"}
                      </Button>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, borderRadius: 3, background: C.bg, textAlign: "center" }}>
                        <Typography sx={{ fontSize: ".78rem", color: C.light }}>הפעלות שנוצלו</Typography>
                        <Typography sx={{ fontWeight: 800, color: C.primary, fontSize: "1.3rem" }}>
                          {isUnlimited ? "♾️" : `${activatedGames} / ${gamesAllowed}`}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, borderRadius: 3, background: C.bg, textAlign: "center" }}>
                        <Typography sx={{ fontSize: ".78rem", color: C.light }}>הפעלות זמינות</Typography>
                        <Typography sx={{ fontWeight: 800, color: isUnlimited ? C.green : (gamesAllowed - activatedGames > 0 ? C.green : C.red), fontSize: "1.3rem" }}>
                          {isUnlimited ? "♾️ ללא הגבלה" : Math.max(0, gamesAllowed - activatedGames)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, borderRadius: 3, background: C.bg, textAlign: "center" }}>
                        <Typography sx={{ fontSize: ".78rem", color: C.light }}>מקס שחקנים</Typography>
                        <Typography sx={{ fontWeight: 800, color: C.blue, fontSize: "1.3rem" }}>{quota.maxPlayersPerGame || 0}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 2, borderRadius: 3, background: C.bg, textAlign: "center" }}>
                        <Typography sx={{ fontSize: ".78rem", color: C.light }}>אחסון</Typography>
                        <Typography sx={{ fontWeight: 800, color: C.amber, fontSize: "1.3rem" }}>{storageUsedMB} / {storageMB} MB</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>

              {/* Purchase history */}
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: C.dark, mb: 1.5 }}>🛒 רכישות</Typography>
              {detail.purchases?.length > 0 ? (
                <StyledCard>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: C.bg }}>
                          <TableCell sx={{ fontWeight: 700 }}>תאריך</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>סכום</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>מזהה עסקה</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>חבילה</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>סטטוס</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.purchases.map((p, i) => (
                          <TableRow key={i} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                            <TableCell sx={{ fontSize: ".85rem" }}>{p.createdAt}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: C.green }}>₪{p.amount || "—"}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace", fontSize: ".82rem", color: C.mid }}>{p.transactionId || "—"}</TableCell>
                            <TableCell sx={{ fontSize: ".85rem" }}>{p.productDescription || p.packageId || "—"}</TableCell>
                            <TableCell>
                              <Chip
                                label={p.processed ? "עובד ✅" : "ממתין ⏳"}
                                size="small"
                                sx={{ fontWeight: 600, background: p.processed ? C.greenBg : C.amberBg, color: p.processed ? C.green : C.amber }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledCard>
              ) : (
                <Box sx={{ p: 5, textAlign: "center", background: C.bg, borderRadius: 3 }}>
                  <ReceiptLongIcon sx={{ fontSize: 48, color: C.light, mb: 1 }} />
                  <Typography sx={{ color: C.light, fontSize: ".95rem" }}>אין רכישות</Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Tab 2 — Purchase History (from user record) */}
          {detailTab === 2 && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: C.dark, mb: 1.5 }}>📋 היסטוריית חבילות שהוענקו</Typography>
              {detail.purchaseHistory?.length > 0 ? (
                <StyledCard>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ background: C.bg }}>
                          <TableCell sx={{ fontWeight: 700 }}>תאריך</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>חבילה</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>סכום</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>הוענקה ע״י</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.purchaseHistory.map((ph, i) => (
                          <TableRow key={i} sx={{ "&:hover": { background: C.primaryLight + "44" } }}>
                            <TableCell sx={{ fontSize: ".85rem" }}>{ph.date}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{ph.packageName}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{ph.amount || "—"}</TableCell>
                            <TableCell sx={{ fontSize: ".85rem", color: C.mid }}>{ph.grantedBy || "מערכת"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </StyledCard>
              ) : (
                <Box sx={{ p: 5, textAlign: "center", background: C.bg, borderRadius: 3 }}>
                  <InventoryIcon sx={{ fontSize: 48, color: C.light, mb: 1 }} />
                  <Typography sx={{ color: C.light, fontSize: ".95rem" }}>אין היסטוריה</Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      ) : null}

      {/* Grant dialog */}
      <Dialog open={grantDialog} onClose={() => setGrantDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🎁 הענקת חבילה — {client.first_name} {client.last_name}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: ".85rem", color: C.mid, mb: 2, mt: 1 }}>בחר חבילה להענקה:</Typography>
          <Box>
            {availablePackages.map(pkg => (
              <Box key={pkg.id} onClick={() => setSelectedPackageId(pkg.id)} sx={{
                p: 2, mb: 1.5, borderRadius: 3,
                border: `2px solid ${selectedPackageId === pkg.id ? C.primary : C.border}`,
                cursor: "pointer", transition: "all .2s",
                background: selectedPackageId === pkg.id ? C.primaryLight : "transparent",
                "&:hover": { borderColor: C.primary + "66" },
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: ".95rem", color: C.dark }}>
                      {pkg.unlimited ? "♾️ " : ""}{pkg.name}
                    </Typography>
                    <Typography sx={{ fontSize: ".78rem", color: C.light, mt: .3 }}>
                      {pkg.unlimited ? "ללא הגבלה" : `${pkg.games} הפעלות`} • עד {pkg.maxPlayers} שחקנים
                      {pkg.storageMB > 0 && ` • +${pkg.storageMB} MB`}
                    </Typography>
                  </Box>
                  {selectedPackageId === pkg.id && <CheckCircleIcon sx={{ color: C.primary }} />}
                </Box>
              </Box>
            ))}
            {availablePackages.length === 0 && (
              <Typography sx={{ textAlign: "center", color: C.light, py: 3 }}>אין חבילות מוגדרות</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGrantDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleGrantPackage}
            disabled={!selectedPackageId}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            הענק חבילה
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>✏️ עריכת לקוח</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editDialog && (
            <Grid container spacing={2} sx={{ mt: .5 }}>
              <Grid item xs={6}><TextField fullWidth label="שם פרטי" value={editDialog.first_name || ""} onChange={e => setEditDialog({ ...editDialog, first_name: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="שם משפחה" value={editDialog.last_name || ""} onChange={e => setEditDialog({ ...editDialog, last_name: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="טלפון" value={editDialog.phone || ""} onChange={e => setEditDialog({ ...editDialog, phone: e.target.value })} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="אימייל" value={editDialog.email || ""} onChange={e => setEditDialog({ ...editDialog, email: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="הערות" multiline rows={2} value={editDialog.notes || ""} onChange={e => setEditDialog({ ...editDialog, notes: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog(null)}>ביטול</Button>
          <Button variant="contained" onClick={handleSaveEdit} sx={{ borderRadius: 3, fontWeight: 700 }}>שמור</Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={pwDialog} onClose={() => { setPwDialog(false); setNewPw(""); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🔑 שינוי סיסמה</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="סיסמה חדשה" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setPwDialog(false); setNewPw(""); }}>ביטול</Button>
          <Button variant="contained" color="warning" onClick={handleResetPw} sx={{ borderRadius: 3, fontWeight: 700 }}>שנה סיסמה</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Package Confirm Dialog */}
      <Dialog open={deletePackageDialog} onClose={() => setDeletePackageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: C.red }}>
          🗑️ מחיקת חבילה — {client.first_name} {client.last_name}
        </DialogTitle>
        <DialogContent>
          {detail?.currentPackageName && (
            <Box sx={{ p: 2, mb: 2, mt: 1, borderRadius: 3, background: C.redBg, border: `1px solid ${C.red}22` }}>
              <Typography sx={{ fontWeight: 700, fontSize: ".95rem", color: C.dark, mb: .3 }}>
                📦 חבילה נוכחית: <span style={{ color: C.primary }}>{detail.currentPackageName}</span>
              </Typography>
              <Typography sx={{ fontSize: ".82rem", color: C.mid }}>
                הפעלות: {isUnlimited ? "♾️" : `${activatedGames} / ${gamesAllowed}`} • שחקנים: {quota.maxPlayersPerGame || 0}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: ".95rem", color: C.dark, mb: 1 }}>
            מחיקת החבילה תאפס את כל מכסות ההפעלות והשחקנים ל-0.
          </Typography>
          <Typography sx={{ fontSize: ".85rem", color: C.red }}>
            ⚠️ שימו לב: הלקוח לא יוכל להפעיל משחקים ללא חבילה חדשה.
          </Typography>

          <Box sx={{ mt: 2.5, p: 2, borderRadius: 3, background: C.bg, border: `1px solid ${C.border}` }}>
            <Typography sx={{ fontWeight: 700, fontSize: ".9rem", color: C.dark, mb: 1 }}>💡 אפשרויות:</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" startIcon={<AutorenewIcon />}
                onClick={handleReplacePackage}
                sx={{ borderRadius: 3, fontWeight: 600, borderColor: C.primary, color: C.primary }}>
                החלף חבילה (במקום למחוק)
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeletePackageDialog(false)}>ביטול</Button>
          <Button variant="contained" color="error" onClick={handleDeletePackage} disabled={deletingPackage}
            sx={{ borderRadius: 3, fontWeight: 700 }}>
            {deletingPackage ? "מוחק..." : "🗑️ מחק חבילה"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ PURCHASES PAGE ═══════════════════ */
const PurchasesPage = ({ loading: externalLoading, onRefresh, snack, clients }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [linkDialog, setLinkDialog] = useState(null); // for linking purchase to user
  const [linkUserId, setLinkUserId] = useState("");

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/purchases");
      setPurchases(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const filtered = purchases.filter(p => {
    const matchSearch = `${p.transactionId || ""} ${p.phone || ""} ${p.email || ""} ${p.userName || ""} ${p.amount || ""} ${p.productDescription || ""}`
      .toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" ||
      (filterStatus === "processed" && p.processed) ||
      (filterStatus === "pending" && !p.processed) ||
      (filterStatus === "unlinked" && !p.user_id);
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    if (!window.confirm("למחוק את הרכישה?")) return;
    const res = await api.del(`/purchases/${id}`);
    if (res.status === "success") {
      snack("רכישה נמחקה", "success");
      fetchPurchases();
    }
  };

  const handleLinkToUser = async () => {
    if (!linkDialog || !linkUserId) return;
    const res = await api.put(`/purchases/${linkDialog.id}`, { user_id: linkUserId });
    if (res.status === "success") {
      snack("רכישה שויכה ללקוח", "success");
      setLinkDialog(null);
      setLinkUserId("");
      fetchPurchases();
    }
  };

  const totalRevenue = purchases.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const processedCount = purchases.filter(p => p.processed).length;
  const pendingCount = purchases.filter(p => !p.processed).length;
  const unlinkedCount = purchases.filter(p => !p.user_id).length;

  if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>🛒 רכישות וחבילות ({purchases.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TextField size="small" placeholder="חיפוש..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.light }} /></InputAdornment> }}
            sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} sx={{ borderRadius: 3 }}>
              <MenuItem value="all">הכל</MenuItem>
              <MenuItem value="processed">עובדו ✅</MenuItem>
              <MenuItem value="pending">ממתינות ⏳</MenuItem>
              <MenuItem value="unlinked">לא משויכות ❌</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchPurchases}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<CreditCardIcon />} label="סה״כ הכנסות" value={`₪${totalRevenue.toLocaleString()}`} color={C.green} bg={C.greenBg} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<ReceiptLongIcon />} label="סה״כ רכישות" value={purchases.length} color={C.primary} bg={C.primaryLight} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<CheckCircleIcon />} label="עובדו" value={processedCount} color={C.green} bg={C.greenBg} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<WarningIcon />} label="ממתינות / לא משויכות" value={`${pendingCount} / ${unlinkedCount}`} color={C.amber} bg={C.amberBg} />
        </Grid>
      </Grid>

      {/* Purchases table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: C.bg }}>
              <TableCell sx={{ fontWeight: 700 }}>תאריך</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>סכום</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>מזהה עסקה</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>לקוח</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>טלפון</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>תיאור</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>סטטוס</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>פעולות</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(purchase => (
              <TableRow key={purchase.id} sx={{
                "&:hover": { background: C.primaryLight + "44" },
                cursor: "pointer",
                background: !purchase.user_id ? C.amberBg + "44" : "inherit",
              }}
                onClick={() => setSelectedPurchase(selectedPurchase?.id === purchase.id ? null : purchase)}
              >
                <TableCell sx={{ fontSize: ".82rem" }}>{purchase.createdAt}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: C.green }}>₪{purchase.amount || "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: ".78rem", color: C.mid }}>{purchase.transactionId || "—"}</TableCell>
                <TableCell>
                  {purchase.userName ? (
                    <Chip label={purchase.userName} size="small" sx={{ fontWeight: 600, background: C.primaryLight, color: C.primary }} />
                  ) : purchase.user_id ? (
                    <Chip label="לקוח משויך" size="small" sx={{ fontWeight: 600, background: C.blueBg, color: C.blue }} />
                  ) : (
                    <Chip label="לא משויך" size="small" sx={{ fontWeight: 600, background: C.redBg, color: C.red }} />
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: ".82rem" }}>{purchase.phone || purchase.userPhone || "—"}</TableCell>
                <TableCell sx={{ fontSize: ".8rem", color: C.mid, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {purchase.productDescription || purchase.packageId || "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={purchase.processed ? "עובד ✅" : purchase.errordescription === "SUCCESS" ? "הצליח ⏳" : purchase.errordescription || "ממתין"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      background: purchase.processed ? C.greenBg : purchase.errordescription === "SUCCESS" ? C.amberBg : C.redBg,
                      color: purchase.processed ? C.green : purchase.errordescription === "SUCCESS" ? C.amber : C.red,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: .3 }} onClick={e => e.stopPropagation()}>
                    {!purchase.user_id && (
                      <Tooltip title="שייך ללקוח">
                        <IconButton size="small" onClick={() => { setLinkDialog(purchase); setLinkUserId(""); }} sx={{ color: C.primary }}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="מחק">
                      <IconButton size="small" onClick={() => handleDelete(purchase.id)} sx={{ color: C.red }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", color: C.light, py: 4 }}>אין רכישות</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Selected purchase detail */}
      {selectedPurchase && (
        <StyledCard sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontWeight: 700, color: C.dark }}>📋 פרטי רכישה</Typography>
              <IconButton size="small" onClick={() => setSelectedPurchase(null)}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Grid container spacing={2}>
              {[
                { label: "מזהה", val: selectedPurchase.id },
                { label: "מזהה עסקה", val: selectedPurchase.transactionId },
                { label: "סכום", val: `₪${selectedPurchase.amount || "—"}` },
                { label: "תאריך", val: selectedPurchase.createdAt },
                { label: "טלפון", val: selectedPurchase.phone },
                { label: "אימייל", val: selectedPurchase.email },
                { label: "שם בכרטיס", val: selectedPurchase.raw?.cardownername },
                { label: "ת.ז.", val: selectedPurchase.raw?.identitynumber },
                { label: "תיאור מוצר", val: selectedPurchase.productDescription },
                { label: "סטטוס עסקה", val: selectedPurchase.errordescription },
                { label: "עובד", val: selectedPurchase.processed ? "כן" : "לא" },
                { label: "לקוח משויך", val: selectedPurchase.userName || (selectedPurchase.user_id ? `משויך (${selectedPurchase.user_id.slice(0,8)}...)` : "לא משויך") },
              ].map((item, i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Typography sx={{ fontSize: ".72rem", color: C.light, mb: .3 }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: ".88rem", fontWeight: 600, color: C.dark, wordBreak: "break-all" }}>{item.val || "—"}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </StyledCard>
      )}

      {/* Link to user dialog */}
      <Dialog open={!!linkDialog} onClose={() => setLinkDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>🔗 שייך רכישה ללקוח</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: ".9rem", color: C.mid }}>
            עסקה: <strong>{linkDialog?.transactionId}</strong> • ₪{linkDialog?.amount}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>בחר לקוח</InputLabel>
            <Select value={linkUserId} label="בחר לקוח" onChange={e => setLinkUserId(e.target.value)} sx={{ borderRadius: 3 }}>
              {clients?.map(c => (
                <MenuItem key={c.user_id} value={c.user_id}>
                  {c.first_name} {c.last_name} — {c.phone || c.email || c.user_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLinkDialog(null)}>ביטול</Button>
          <Button variant="contained" onClick={handleLinkToUser} disabled={!linkUserId}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            שייך
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ PACKAGES MANAGEMENT PAGE ═══════════════════ */
const PKG_TYPES = [
  { id: 'game', label: '🎮 משחקים ושחקנים', color: '#4f46e5' },
  { id: 'storage', label: '💾 אחסון', color: '#0891b2' },
  { id: 'sms', label: '📩 הודעות SMS', color: '#7c3aed' },
  { id: 'football', label: '⚽ כדורגל', color: '#16a34a' },
];

const PackagesManagementPage = ({ snack }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [activeType, setActiveType] = useState('game');
  const [formData, setFormData] = useState({
    name: "", type: "game", games: 1, maxPlayers: 12, storageMB: 0, smsCredits: 0,
    price: 0, payLinkParam: "", active: true, unlimited: false, isPublic: true, notes: "", order: 50,
    maxPerCustomer: 0, isDemo: false,
  });

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try { setPackages(await api.get("/packages")); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const resetForm = (type) => setFormData({
    name: "", type: type || activeType, games: type === 'game' ? 1 : 0, maxPlayers: type === 'game' ? 12 : 0,
    storageMB: type === 'storage' ? 100 : 0, smsCredits: type === 'sms' ? 50 : 0,
    price: 0, payLinkParam: "", active: true, unlimited: false, isPublic: true, notes: "", order: 50,
    maxPerCustomer: 0, isDemo: false,
  });

  const handleSave = async () => {
    if (editDialog) {
      const { id, ...rest } = formData;
      const res = await api.put(`/packages/${editDialog}`, rest);
      if (res.status === "success") { snack("חבילה עודכנה", "success"); setEditDialog(null); fetchPackages(); }
      else snack(res.error || "שגיאה", "error");
    } else {
      if (!formData.name) { snack("חסר שם חבילה", "warning"); return; }
      const res = await api.post("/packages", formData);
      if (res.status === "success") {
        snack(`חבילה נוספה (${res.package?.id || ''})`, "success");
        setAddDialog(false); resetForm(); fetchPackages();
      }
      else snack(res.error || "שגיאה", "error");
    }
  };

  // ===== Confirm Dialog State =====
  const [confirmDialog, setConfirmDialog] = useState(null);
  // { title, message, icon, color, confirmText, cancelText, onConfirm, extraAction? }

  const showConfirm = (opts) => new Promise(resolve => {
    setConfirmDialog({ ...opts, onConfirm: () => { setConfirmDialog(null); resolve(true); }, onCancel: () => { setConfirmDialog(null); resolve(false); } });
  });

  const handleDelete = async (pkgId) => {
    const pkg = packages.find(p => p.id === pkgId);
    const confirmed = await showConfirm({
      title: "🗑️ מחיקת חבילה",
      message: `האם למחוק את החבילה "${pkg?.name || pkgId}"?\nפעולה זו לא ניתנת לביטול.`,
      icon: <DeleteIcon sx={{ fontSize: 48, color: C.red }} />,
      color: C.red,
      confirmText: "מחק חבילה",
    });
    if (!confirmed) return;

    let deletePayment = false;
    if (pkg?.payLinkParam) {
      deletePayment = await showConfirm({
        title: "💳 עמוד תשלום קיים",
        message: `לחבילה זו יש עמוד תשלום פעיל ב-UPay.\nהאם למחוק גם את עמוד התשלום?`,
        icon: <MoneyOffIcon sx={{ fontSize: 48, color: C.amber }} />,
        color: C.amber,
        confirmText: "כן, מחק גם תשלום",
        cancelText: "לא, רק החבילה",
      });
    }

    try {
      const path = deletePayment ? `/packages/${pkgId}?deletePayment=true` : `/packages/${pkgId}`;
      await api.del(path);
      snack(deletePayment ? "חבילה + עמוד תשלום נמחקו" : "חבילה נמחקה", "success");
    } catch {
      snack("שגיאה במחיקה", "error");
    }
    fetchPackages();
  };

  const [paymentLoading, setPaymentLoading] = useState(null);

  const handleCreatePayment = async (pkgId) => {
    setPaymentLoading(pkgId);
    try {
      const res = await api.post(`/packages/${pkgId}/create-payment`, {});
      if (res.status === "success") {
        snack("עמוד תשלום נוצר בהצלחה!", "success");
        // עדכון הפורם אם אנחנו בעריכה
        if (editDialog === pkgId) {
          setFormData(prev => ({ ...prev, payLinkParam: res.payLinkParam }));
        }
        fetchPackages();
      } else {
        snack(res.error || "שגיאה ביצירת עמוד תשלום", "error");
      }
    } catch (e) {
      snack("שגיאה ביצירת עמוד תשלום", "error");
    }
    setPaymentLoading(null);
  };

  const handleDeletePayment = async (pkgId) => {
    const confirmed = await showConfirm({
      title: "🚫 מחיקת עמוד תשלום",
      message: "האם למחוק את עמוד התשלום של חבילה זו מ-UPay?\nהקישור יפסיק לעבוד.",
      icon: <MoneyOffIcon sx={{ fontSize: 48, color: C.red }} />,
      color: C.red,
      confirmText: "מחק עמוד תשלום",
    });
    if (!confirmed) return;
    setPaymentLoading(pkgId);
    try {
      const res = await api.post(`/packages/${pkgId}/delete-payment`, {});
      if (res.status === "success") {
        snack("עמוד תשלום נמחק", "success");
        if (editDialog === pkgId) {
          setFormData(prev => ({ ...prev, payLinkParam: "" }));
        }
        fetchPackages();
      } else {
        snack(res.error || "שגיאה במחיקת עמוד תשלום", "error");
      }
    } catch {
      snack("שגיאה במחיקת עמוד תשלום", "error");
    }
    setPaymentLoading(null);
  };

  const handleReplacePayment = async (pkgId) => {
    const confirmed = await showConfirm({
      title: "🔄 החלפת עמוד תשלום",
      message: "עמוד התשלום הקיים יימחק ויווצר עמוד חדש.\nלהמשיך?",
      icon: <AutorenewIcon sx={{ fontSize: 48, color: C.primary }} />,
      color: C.primary,
      confirmText: "החלף עמוד תשלום",
    });
    if (!confirmed) return;
    setPaymentLoading(pkgId);
    try {
      // מחיקה
      await api.post(`/packages/${pkgId}/delete-payment`, {});
      // יצירה מחדש
      const res = await api.post(`/packages/${pkgId}/create-payment`, {});
      if (res.status === "success") {
        snack("עמוד תשלום הוחלף בהצלחה!", "success");
        if (editDialog === pkgId) {
          setFormData(prev => ({ ...prev, payLinkParam: res.payLinkParam }));
        }
        fetchPackages();
      } else {
        snack(res.error || "שגיאה בהחלפת עמוד תשלום", "error");
      }
    } catch {
      snack("שגיאה בהחלפת עמוד תשלום", "error");
    }
    setPaymentLoading(null);
  };

  const handleToggleActive = async (pkg) => {
    await api.put(`/packages/${pkg.id}`, { active: !pkg.active });
    fetchPackages();
  };

  if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;

  const filteredPackages = packages.filter(p => (p.type || 'game') === activeType);
  const typeInfo = PKG_TYPES.find(t => t.id === activeType);

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>📦 ניהול חבילות ({packages.length})</Typography>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(activeType); setAddDialog(true); }}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${typeInfo.color}, ${typeInfo.color}cc)` }}>
            חבילה חדשה
          </Button>
          <IconButton onClick={fetchPackages}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={activeType} onChange={(_, v) => setActiveType(v)}
        sx={{
          mb: 3, '& .MuiTab-root': { fontWeight: 700, fontSize: '.82rem', borderRadius: '12px 12px 0 0', minHeight: 44 },
          '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
        }}>
        {PKG_TYPES.map(t => (
          <Tab key={t.id} value={t.id} label={`${t.label} (${packages.filter(p => (p.type || 'game') === t.id).length})`} />
        ))}
      </Tabs>

      <Grid container spacing={2.5}>
        {filteredPackages.map(pkg => (
          <Grid item xs={12} sm={6} lg={4} key={pkg.id}>
            <StyledCard sx={{ opacity: pkg.active ? 1 : .6 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: C.dark }}>
                      {pkg.unlimited ? '♾️ ' : activeType === 'sms' ? '📩 ' : activeType === 'storage' ? '💾 ' : '📦 '}{pkg.name}
                    </Typography>
                    <Typography sx={{ fontSize: ".72rem", color: C.light }}>ID: {pkg.id}</Typography>
                    {pkg.notes && <Typography sx={{ fontSize: ".72rem", color: C.mid, mt: .3 }}>📝 {pkg.notes}</Typography>}
                  </Box>
                  <Chip
                    label={pkg.active ? "פעילה" : "מושבתת"}
                    size="small"
                    onClick={() => handleToggleActive(pkg)}
                    sx={{
                      cursor: "pointer", fontWeight: 600, fontSize: ".7rem",
                      background: pkg.active ? C.greenBg : C.redBg,
                      color: pkg.active ? C.green : C.red,
                    }}
                  />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {activeType === 'game' && (
                    <>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                          <Typography sx={{ fontSize: ".65rem", color: C.light }}>הפעלות</Typography>
                          <Typography sx={{ fontWeight: 800, color: C.primary }}>{pkg.unlimited || pkg.games === -1 ? "∞" : pkg.games}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                          <Typography sx={{ fontSize: ".65rem", color: C.light }}>שחקנים</Typography>
                          <Typography sx={{ fontWeight: 800, color: C.blue }}>{pkg.maxPlayers}</Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                  {activeType === 'storage' && (
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                        <Typography sx={{ fontSize: ".65rem", color: C.light }}>אחסון</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#0891b2' }}>
                          {pkg.unlimited || pkg.storageMB === -1 ? '∞' : pkg.storageMB >= 1024 ? `${(pkg.storageMB / 1024).toFixed(1)} GB` : `${pkg.storageMB} MB`}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {activeType === 'sms' && (
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                        <Typography sx={{ fontSize: ".65rem", color: C.light }}>הודעות</Typography>
                        <Typography sx={{ fontWeight: 800, color: '#7c3aed' }}>
                          {pkg.unlimited || pkg.smsCredits === -1 ? '∞' : pkg.smsCredits || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={activeType === 'game' ? 4 : 6}>
                    <Box sx={{ textAlign: "center", p: 1, borderRadius: 2, background: C.bg }}>
                      <Typography sx={{ fontSize: ".65rem", color: C.light }}>מחיר</Typography>
                      <Typography sx={{ fontWeight: 800, color: C.green }}>{pkg.price > 0 ? `₪${pkg.price}` : "חינם"}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", gap: .5, flexWrap: "wrap" }}>
                    <Chip
                      label={pkg.isPublic !== false ? "👁️ ציבורית" : "🔒 מוסתרת"}
                      size="small"
                      sx={{
                        fontSize: ".68rem", fontWeight: 600,
                        background: pkg.isPublic !== false ? C.blueBg : C.purpleBg,
                        color: pkg.isPublic !== false ? C.blue : C.purple,
                      }}
                    />
                    {pkg.payLinkParam ? (
                      <Chip label="✅ קישור תשלום" size="small" sx={{ fontSize: ".68rem", fontWeight: 600, background: C.greenBg, color: C.green, cursor: "pointer" }}
                        onClick={() => window.open(`https://app.upay.co.il/t/?m=${pkg.payLinkParam}`, "_blank")} />
                    ) : pkg.price <= 0 ? (
                      <Chip label="🎁 חינם" size="small" sx={{ fontSize: ".68rem", fontWeight: 600, background: '#ecfdf5', color: '#059669' }} />
                    ) : (
                      <Chip label="❌ ללא קישור" size="small" sx={{ fontSize: ".68rem", fontWeight: 600, background: C.amberBg, color: C.amber }} />
                    )}
                    {pkg.isDemo && (
                      <Chip label="🎯 הדגמה" size="small" sx={{ fontSize: ".68rem", fontWeight: 600, background: '#fffbeb', color: '#d97706' }} />
                    )}
                    {pkg.maxPerCustomer > 0 && (
                      <Chip label={`🔒 מקס ${pkg.maxPerCustomer}x`} size="small" sx={{ fontSize: ".68rem", fontWeight: 600, background: '#fef2f2', color: '#dc2626' }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: .3 }}>
                    {/* Payment actions */}
                    {!pkg.payLinkParam ? (
                      <Tooltip title="צור עמוד תשלום">
                        <IconButton size="small" onClick={() => handleCreatePayment(pkg.id)}
                          disabled={paymentLoading === pkg.id || pkg.price <= 0}
                          sx={{ color: C.green }}>
                          {paymentLoading === pkg.id ? <CircularProgress size={18} /> : <PaymentIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="מחק עמוד תשלום">
                        <IconButton size="small" onClick={() => handleDeletePayment(pkg.id)}
                          disabled={paymentLoading === pkg.id}
                          sx={{ color: C.amber }}>
                          {paymentLoading === pkg.id ? <CircularProgress size={18} /> : <MoneyOffIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="עריכה">
                      <IconButton size="small" onClick={() => {
                        setFormData({ ...pkg });
                        setEditDialog(pkg.id);
                      }}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="מחיקה">
                      <IconButton size="small" onClick={() => handleDelete(pkg.id)} sx={{ color: C.red }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={addDialog || !!editDialog} onClose={() => { setAddDialog(false); setEditDialog(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{editDialog ? "✏️ עריכת חבילה" : `➕ חבילה חדשה — ${PKG_TYPES.find(t => t.id === (formData.type || 'game'))?.label}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: .5 }}>
            {/* Type selector (only on add) */}
            {!editDialog && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {PKG_TYPES.map(t => (
                    <Button key={t.id} fullWidth
                      variant={formData.type === t.id ? "contained" : "outlined"}
                      onClick={() => { resetForm(t.id); }}
                      sx={{
                        borderRadius: 3, fontWeight: 700, fontSize: ".78rem", py: 1.2,
                        ...(formData.type === t.id ? { background: `linear-gradient(135deg, ${t.color}, ${t.color}cc)` } : { color: t.color, borderColor: `${t.color}55` }),
                      }}>
                      {t.label}
                    </Button>
                  ))}
                </Box>
              </Grid>
            )}
            {editDialog ? (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="מזהה (ID)" value={formData.id || editDialog}
                    InputProps={{ readOnly: true }}
                    sx={{ "& .MuiOutlinedInput-root": { background: C.bg } }}
                    helperText="לא ניתן לשינוי" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="שם החבילה" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <Box sx={{ p: 2, borderRadius: 3, background: C.primaryLight, border: `1px solid ${C.primary}33`, height: '100%', display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: ".82rem", color: C.primary, fontWeight: 600 }}>
                      🔑 מזהה ייוצר אוטומטית
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="שם החבילה" value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField fullWidth label="הערות" value={formData.notes || ""} multiline rows={2}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="הערות פנימיות על החבילה..." />
            </Grid>

            {/* ==== Fields per type ==== */}
            {/* ==== שדות לפי סוג חבילה ==== */}
            {(formData.type || 'game') === 'game' && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth label="כמות הפעלות" type="number" value={formData.unlimited ? '' : formData.games}
                    onChange={e => setFormData({ ...formData, games: parseInt(e.target.value) || 0 })}
                    disabled={formData.unlimited}
                    helperText={formData.unlimited ? '♾️ ללא הגבלה' : 'מספר הפעלות'}
                    InputProps={{ sx: formData.unlimited ? { background: C.bg, '& input': { color: C.light } } : {} }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="מקס שחקנים" type="number" value={formData.maxPlayers}
                    onChange={e => setFormData({ ...formData, maxPlayers: parseInt(e.target.value) || 0 })} />
                </Grid>
              </>
            )}
            {(formData.type || 'game') === 'storage' && (
              <Grid item xs={12}>
                <TextField fullWidth label="אחסון (MB)" type="number" value={formData.unlimited ? '' : formData.storageMB}
                  onChange={e => setFormData({ ...formData, storageMB: parseInt(e.target.value) || 0 })}
                  disabled={formData.unlimited}
                  helperText={formData.unlimited ? '♾️ ללא הגבלה' : '1024 MB = 1 GB'}
                  InputProps={{ sx: formData.unlimited ? { background: C.bg, '& input': { color: C.light } } : {} }} />
              </Grid>
            )}
            {(formData.type || 'game') === 'sms' && (
              <Grid item xs={12}>
                <TextField fullWidth label="כמות הודעות SMS" type="number" value={formData.unlimited ? '' : formData.smsCredits}
                  onChange={e => setFormData({ ...formData, smsCredits: parseInt(e.target.value) || 0 })}
                  disabled={formData.unlimited}
                  helperText={formData.unlimited ? '♾️ ללא הגבלה' : 'מספר הודעות SMS שיתווספו ליתרה'}
                  InputProps={{ sx: formData.unlimited ? { background: C.bg, '& input': { color: C.light } } : {} }} />
              </Grid>
            )}
            <Grid item xs={4}>
              <TextField fullWidth label="מחיר (₪)" type="number" value={formData.price}
                onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth label="סדר תצוגה" type="number" value={formData.order}
                onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField fullWidth label="פרמטר קישור תשלום (m=)" value={formData.payLinkParam}
                  onChange={e => setFormData({ ...formData, payLinkParam: e.target.value })}
                  helperText={formData.payLinkParam ? "✅ קישור תשלום מוגדר" : "השאר ריק או צור עמוד תשלום אוטומטית"}
                  InputProps={{
                    readOnly: !!formData.payLinkParam,
                    sx: formData.payLinkParam ? { background: C.greenBg, "& input": { color: C.green, fontWeight: 700 } } : {}
                  }} />
                {editDialog && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: .5, mt: .5, minWidth: 130 }}>
                    {!formData.payLinkParam ? (
                      <Button
                        variant="contained" size="small"
                        startIcon={paymentLoading === editDialog ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
                        disabled={paymentLoading === editDialog || !formData.price || formData.price <= 0}
                        onClick={() => handleCreatePayment(editDialog)}
                        sx={{ borderRadius: 2, fontWeight: 700, fontSize: ".75rem", py: .8, background: `linear-gradient(135deg, ${C.green}, #059669)`, whiteSpace: "nowrap" }}>
                        צור עמוד תשלום
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained" size="small"
                          startIcon={paymentLoading === editDialog ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                          disabled={paymentLoading === editDialog}
                          onClick={() => handleReplacePayment(editDialog)}
                          sx={{ borderRadius: 2, fontWeight: 700, fontSize: ".72rem", py: .6, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)`, whiteSpace: "nowrap" }}>
                          החלף עמוד
                        </Button>
                        <Button
                          variant="outlined" size="small" color="error"
                          startIcon={paymentLoading === editDialog ? <CircularProgress size={16} /> : <MoneyOffIcon />}
                          disabled={paymentLoading === editDialog}
                          onClick={() => handleDeletePayment(editDialog)}
                          sx={{ borderRadius: 2, fontWeight: 600, fontSize: ".72rem", py: .4, whiteSpace: "nowrap" }}>
                          מחק עמוד
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth variant={formData.unlimited ? "contained" : "outlined"}
                onClick={() => {
                  const type = formData.type || 'game';
                  const next = !formData.unlimited;
                  const patch = { unlimited: next };
                  if (type === 'game') patch.games = next ? -1 : 1;
                  if (type === 'storage') patch.storageMB = next ? -1 : 100;
                  if (type === 'sms') patch.smsCredits = next ? -1 : 50;
                  setFormData({ ...formData, ...patch });
                }}
                sx={{ borderRadius: 3, fontWeight: 700, py: 1.5, ...(formData.unlimited ? { background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` } : {}) }}>
                {formData.unlimited ? "♾️ ללא הגבלה" : "ללא הגבלה"}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth variant={formData.active ? "contained" : "outlined"} color={formData.active ? "success" : "error"}
                onClick={() => setFormData({ ...formData, active: !formData.active })}
                sx={{ borderRadius: 3, fontWeight: 700, py: 1.5 }}>
                {formData.active ? "✅ פעילה" : "❌ מושבתת"}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button fullWidth variant={formData.isPublic !== false ? "contained" : "outlined"}
                onClick={() => setFormData({ ...formData, isPublic: formData.isPublic === false ? true : false })}
                sx={{ borderRadius: 3, fontWeight: 700, py: 1.5, ...(formData.isPublic !== false ? { background: `linear-gradient(135deg, ${C.blue}, #2563eb)` } : { color: C.purple, borderColor: C.purple }) }}>
                {formData.isPublic !== false ? "👁️ ציבורית" : "🔒 מוסתרת"}
              </Button>
            </Grid>

            {/* הגבלות מתקדמות */}
            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, fontSize: '.85rem', color: C.dark, mt: 1, mb: .5 }}>⚙️ הגבלות מתקדמות</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="מגבלת מימושים ללקוח" type="number" value={formData.maxPerCustomer || 0}
                onChange={e => setFormData({ ...formData, maxPerCustomer: parseInt(e.target.value) || 0 })}
                helperText={formData.maxPerCustomer > 0 ? `כל לקוח יכול לממש עד ${formData.maxPerCustomer} פעמים` : '0 = ללא הגבלה'} />
            </Grid>
            <Grid item xs={6}>
              <Button fullWidth variant={formData.isDemo ? "contained" : "outlined"}
                onClick={() => setFormData({ ...formData, isDemo: !formData.isDemo })}
                sx={{ borderRadius: 3, fontWeight: 700, py: 1.5, mt: .5,
                  ...(formData.isDemo ? { background: `linear-gradient(135deg, #f59e0b, #d97706)` } : { color: '#f59e0b', borderColor: '#f59e0b55' })
                }}>
                {formData.isDemo ? "🎯 חבילת הדגמה" : "חבילת הדגמה"}
              </Button>
              {formData.isDemo && (
                <Typography sx={{ fontSize: '.68rem', color: C.mid, mt: .5, textAlign: 'center' }}>
                  תוצג רק ללקוחות חדשים שלא רכשו עדיין
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setAddDialog(false); setEditDialog(null); }}>ביטול</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ borderRadius: 3, fontWeight: 700, background: `linear-gradient(135deg, ${C.primary}, #7c3aed)` }}>
            {editDialog ? "שמור" : "הוסף חבילה"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Confirm Dialog ===== */}
      <Dialog open={!!confirmDialog} onClose={confirmDialog?.onCancel} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
        {confirmDialog && (
          <>
            <Box sx={{ textAlign: "center", pt: 4, pb: 1 }}>
              {confirmDialog.icon}
              <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: C.dark, mt: 1.5 }}>
                {confirmDialog.title}
              </Typography>
            </Box>
            <DialogContent sx={{ textAlign: "center", pb: 1 }}>
              <Typography sx={{ color: C.mid, fontSize: ".9rem", whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {confirmDialog.message}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 3, px: 3 }}>
              <Button onClick={confirmDialog.onCancel}
                sx={{ borderRadius: 3, fontWeight: 600, px: 3, color: C.mid }}>
                {confirmDialog.cancelText || "ביטול"}
              </Button>
              <Button variant="contained" onClick={confirmDialog.onConfirm}
                sx={{ borderRadius: 3, fontWeight: 700, px: 3,
                  background: `linear-gradient(135deg, ${confirmDialog.color || C.primary}, ${confirmDialog.color || C.primary}dd)`,
                  "&:hover": { background: `linear-gradient(135deg, ${confirmDialog.color || C.primary}dd, ${confirmDialog.color || C.primary})` }
                }}>
                {confirmDialog.confirmText || "אישור"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ MAIN CRM PANEL ═══════════════════ */
/* ═══════════════════ ניהול (מנהל ראשי) ═══════════════════ */
// לשונית ניהול מרכזת את כל מה ששייך למנהל הראשי: תיבת הדואר המלאה (נכנס+יוצא)
// וניהול כתובות הדוא"ל בדומיין. נגישה למנהל בלבד (adminOnly בסיידבר).
const ManagePage = ({ snack, onSyncTickets }) => {
  const [subTab, setSubTab] = useState("mail");

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <SettingsIcon sx={{ color: C.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>ניהול ראשי</Typography>
      </Box>

      <Tabs
        value={subTab}
        onChange={(e, v) => setSubTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": { fontWeight: 700, fontSize: ".95rem", borderRadius: 2, minHeight: 44 },
          "& .Mui-selected": { color: `${C.primary} !important` },
          "& .MuiTabs-indicator": { backgroundColor: C.primary, height: 3, borderRadius: 3 },
        }}
      >
        <Tab value="mail" icon={<EmailIcon fontSize="small" />} iconPosition="start" label="דוא&quot;ל" />
        <Tab value="mailboxes" icon={<AlternateEmailIcon fontSize="small" />} iconPosition="start" label="ניהול כתובות דוא&quot;ל" />
        <Tab value="gemini" icon={<VpnKeyIcon fontSize="small" />} iconPosition="start" label="מפתחות Gemini" />
      </Tabs>

      {subTab === "mail" && <EmailPage snack={snack} onSyncTickets={onSyncTickets} />}
      {subTab === "mailboxes" && <MailboxesPage snack={snack} />}
      {subTab === "gemini" && <GeminiKeysPage snack={snack} />}
    </Box>
  );
};

/* ═══════════════════ מפתחות Gemini (ניהול) ═══════════════════ */
const GeminiKeysPage = ({ snack }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", key: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/gemini-keys");
      setKeys(data.keys || []);
    } catch { snack("שגיאה בטעינת מפתחות", "error"); }
    setLoading(false);
  }, [snack]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim() || !form.key.trim()) { snack("שם ומפתח הם חובה", "error"); return; }
    try {
      await api.post("/gemini-keys", form);
      snack("המפתח נוסף");
      setDlg(false); setForm({ name: "", email: "", key: "" }); load();
    } catch { snack("שגיאה בהוספה", "error"); }
  };

  const del = async (id) => {
    if (!window.confirm("למחוק את המפתח?")) return;
    try { await api.del(`/gemini-keys/${id}`); snack("נמחק"); load(); }
    catch { snack("שגיאה במחיקה", "error"); }
  };

  const enable = async (id) => {
    try { await api.post(`/gemini-keys/${id}/enable`); snack("הופעל מחדש"); load(); }
    catch { snack("שגיאה", "error"); }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: 700, color: C.dark }}>מפתחות Gemini ({keys.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg(true)} sx={{ borderRadius: 2, fontWeight: 700 }}>הוסף מפתח</Button>
      </Box>

      {loading ? <Typography sx={{ color: C.light }}>טוען…</Typography> : keys.length === 0 ? (
        <Typography sx={{ color: C.light }}>אין מפתחות עדיין. הוסף מפתח כדי לאפשר יצירת שאלות ב-AI.</Typography>
      ) : (
        <Grid container spacing={2}>
          {keys.map(k => (
            <Grid item xs={12} md={6} key={k.id}>
              <Card sx={{ borderRadius: 3, border: `1px solid ${k.disabled ? C.redBg : "#eee"}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <VpnKeyIcon sx={{ color: C.primary, fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 800 }}>{k.name}</Typography>
                        {k.disabled && <Chip size="small" label="מושבת" sx={{ background: C.redBg, color: C.red, fontWeight: 700 }} />}
                      </Box>
                      <Typography sx={{ color: C.light, fontSize: ".85rem", mt: .5 }}>{k.email || "— ללא מייל —"}</Typography>
                      <Typography sx={{ color: C.light, fontSize: ".8rem", fontFamily: "monospace", mt: .5 }}>{k.keyMasked}</Typography>
                      {k.disabled && k.disabledReason && <Typography sx={{ color: C.red, fontSize: ".75rem", mt: .5 }}>{k.disabledReason}</Typography>}
                    </Box>
                    <Box sx={{ display: "flex", gap: .5 }}>
                      {k.disabled && <Button size="small" onClick={() => enable(k.id)} sx={{ fontWeight: 700 }}>הפעל</Button>}
                      <IconButton size="small" color="error" onClick={() => del(k.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>הוספת מפתח Gemini</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField label="שם המפתח" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth autoFocus />
          <TextField label="מייל משויך" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} fullWidth />
          <TextField label="מפתח API" value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} fullWidth multiline />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDlg(false)}>ביטול</Button>
          <Button variant="contained" onClick={add} sx={{ fontWeight: 700 }}>הוסף</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ═══════════════════ הענקת קרדיטים ל-AI ═══════════════════ */
const GrantCreditsForm = ({ snack }) => {
  const [target, setTarget] = useState("");
  const [amount, setAmount] = useState(100);
  const [busy, setBusy] = useState(false);

  const grant = async () => {
    if (!target.trim() || !amount) { snack("יש להזין לקוח וכמות", "error"); return; }
    setBusy(true);
    try {
      const d = await api.post("/ai-grant-credits", { target: target.trim(), amount: Number(amount) });
      snack(`עודכן — ${d.name}: ${d.aiCredits} קרדיטים`);
      setTarget("");
    } catch (e) { snack(e?.message || "שגיאה בהענקת קרדיטים", "error"); }
    setBusy(false);
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
      <TextField size="small" label="לקוח (user_id / מייל / טלפון)" value={target} onChange={e => setTarget(e.target.value)} sx={{ flex: "1 1 280px" }} />
      <TextField size="small" label="כמות" type="number" value={amount} onChange={e => setAmount(e.target.value)} sx={{ width: 120 }} />
      <Button variant="contained" onClick={grant} disabled={busy} sx={{ borderRadius: 2, fontWeight: 700 }}>הענק</Button>
      <Typography sx={{ color: C.light, fontSize: ".8rem", width: "100%" }}>אפשר גם ערך שלילי כדי להפחית. (גשר זמני עד לחיבור החבילות בתשלום.)</Typography>
    </Box>
  );
};

/* ═══════════════════ לוח בקרה AI ═══════════════════ */
const AiDashboardPage = ({ snack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get("/ai-dashboard")); }
    catch { snack("שגיאה בטעינת נתוני AI", "error"); }
    setLoading(false);
  }, [snack]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Typography sx={{ color: C.light }}>טוען…</Typography>;
  if (!data) return null;

  const t = data.totals || {};
  // קיבוץ שימוש לכל מפתח לכל יום למטריצה נוחה
  const usage = data.usage || [];

  return (
    <Box sx={{ animation: `${fadeIn} .4s ease` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <AutoAwesomeIcon sx={{ color: C.primary }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: C.dark }}>לוח בקרה AI</Typography>
        <Button size="small" onClick={load} sx={{ ml: "auto", fontWeight: 700 }}>רענן</Button>
      </Box>

      {/* סיכומים */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><StatCard icon={<QuizIcon />} label="סה״כ שאלות שנוצרו" value={(t.questions || 0).toLocaleString()} color={C.purple} bg={C.purpleBg} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<AutoAwesomeIcon />} label="בקשות" value={(t.requests || 0).toLocaleString()} color={C.primary} bg={C.primaryLight} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<InboxIcon />} label="שגיאות" value={(t.errors || 0).toLocaleString()} color={C.red} bg={C.redBg} /></Grid>
        <Grid item xs={6} md={3}><StatCard icon={<VpnKeyIcon />} label="מפתחות" value={(data.keys || []).length} color={C.green} bg={C.greenBg} /></Grid>
      </Grid>

      {/* הענקת קרדיטים ללקוח */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>הענקת קרדיטים ליצירת שאלות (AI)</Typography>
          <GrantCreditsForm snack={snack} />
        </CardContent>
      </Card>

      {/* בעיות מפתחות */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>סטטוס מפתחות</Typography>
          {(data.keys || []).length === 0 ? <Typography sx={{ color: C.light }}>אין מפתחות</Typography> :
            (data.keys || []).map(k => (
              <Box key={k.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: .8, borderBottom: "1px solid #f2f2f2" }}>
                <VpnKeyIcon sx={{ fontSize: 18, color: k.disabled ? C.red : C.green }} />
                <Typography sx={{ fontWeight: 700 }}>{k.name}</Typography>
                <Typography sx={{ color: C.light, fontSize: ".8rem" }}>{k.email}</Typography>
                {k.disabled
                  ? <Chip size="small" label={k.disabledReason || "מושבת"} sx={{ ml: "auto", background: C.redBg, color: C.red, fontWeight: 700, maxWidth: 380 }} />
                  : <Chip size="small" label="פעיל" sx={{ ml: "auto", background: C.greenBg, color: C.green, fontWeight: 700 }} />}
              </Box>
            ))}
        </CardContent>
      </Card>

      {/* שימוש יומי לכל מפתח */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>שימוש יומי לפי מפתח</Typography>
          {usage.length === 0 ? <Typography sx={{ color: C.light }}>אין נתוני שימוש עדיין</Typography> : (
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { textAlign: "start", p: 1, borderBottom: "1px solid #f0f0f0", fontSize: ".88rem" }, "& th": { color: C.light, fontWeight: 700 } }}>
                <thead><tr><th>תאריך</th><th>מפתח</th><th>שאלות</th><th>בקשות</th><th>שגיאות</th></tr></thead>
                <tbody>
                  {usage.map((u, i) => (
                    <tr key={i}>
                      <td>{u.day}</td>
                      <td style={{ fontWeight: 700 }}>{u.keyName}</td>
                      <td>{u.questions}</td>
                      <td>{u.requests}</td>
                      <td style={{ color: u.errors ? C.red : "inherit" }}>{u.errors}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* אירועים / בעיות אחרונות */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>אירועים אחרונים (שגיאות / מגבלות)</Typography>
          {(data.events || []).length === 0 ? <Typography sx={{ color: C.light }}>אין אירועים</Typography> :
            (data.events || []).map((e, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: .7, borderBottom: "1px solid #f5f5f5" }}>
                <Chip size="small" label={e.type} sx={{ background: e.type === "quota" ? C.amberBg : C.redBg, color: e.type === "quota" ? C.amber : C.red, fontWeight: 700 }} />
                <Typography sx={{ fontWeight: 700, fontSize: ".85rem" }}>{e.keyName || "—"}</Typography>
                <Typography sx={{ color: C.light, fontSize: ".82rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{e.message}</Typography>
                <Typography sx={{ color: C.light, fontSize: ".75rem" }}>{e.at ? new Date(e.at).toLocaleString("he-IL") : ""}</Typography>
              </Box>
            ))}
        </CardContent>
      </Card>
    </Box>
  );
};

const CrmPanel = () => {
  const [authed, setAuthed] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clients, setClients] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [phones, setPhones] = useState([]);
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState({});
  const [snackState, setSnackState] = useState({ open: false, msg: "", sev: "success" });

  const showSnack = useCallback((msg, sev = "success") => {
    setSnackState({ open: true, msg, sev });
  }, []);

  // Check stored token
  useEffect(() => {
    const saved = localStorage.getItem("crm_token");
    if (saved) {
      api.token = saved;
      try {
        const agentData = JSON.parse(localStorage.getItem("crm_agent") || "null");
        setCurrentAgent(agentData);
      } catch { setCurrentAgent(null); }
      setAuthed(true);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    setLoading(prev => ({ ...prev, clients: true }));
    try { setClients(await api.get("/clients")); } catch {}
    setLoading(prev => ({ ...prev, clients: false }));
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(prev => ({ ...prev, tickets: true }));
    try { setTickets(await api.get("/tickets")); } catch {}
    setLoading(prev => ({ ...prev, tickets: false }));
  }, []);

  const fetchPhones = useCallback(async () => {
    setLoading(prev => ({ ...prev, phones: true }));
    try { setPhones(await api.get("/phones")); } catch {}
    setLoading(prev => ({ ...prev, phones: false }));
  }, []);

  const fetchAgents = useCallback(async () => {
    setLoading(prev => ({ ...prev, agents: true }));
    try { setAgents(await api.get("/agents")); } catch {}
    setLoading(prev => ({ ...prev, agents: false }));
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(prev => ({ ...prev, stats: true }));
    try { setStats(await api.get("/stats")); } catch {}
    setLoading(prev => ({ ...prev, stats: false }));
  }, []);

  const fetchGames = useCallback(async () => {
    setLoading(prev => ({ ...prev, games: true }));
    try { setGames(await api.get("/games")); } catch {}
    setLoading(prev => ({ ...prev, games: false }));
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (!authed) return;
    if (activeTab === "dashboard" || activeTab === "stats") fetchStats();
    if (activeTab === "clients" || activeTab === "dashboard") fetchClients();
    if (activeTab === "tickets") { fetchTickets(); fetchAgents(); fetchClients(); }
    if (activeTab === "phones") fetchPhones();
    if (activeTab === "agents") fetchAgents();
    if (activeTab === "games") fetchGames();
    if (activeTab === "purchases") fetchClients();
  }, [authed, activeTab, fetchStats, fetchClients, fetchTickets, fetchPhones, fetchGames, fetchAgents]);

  const handleLogin = useCallback((agentData, role) => {
    setCurrentAgent(agentData);
    setAuthed(true);
  }, []);

  const handleViewClient = useCallback((clientUserId) => {
    setSelectedClientId(clientUserId);
    setActiveTab("clientDetail");
  }, []);

  const handleBackFromClient = useCallback(() => {
    setActiveTab("clients");
    setSelectedClientId(null);
  }, []);

  if (!authed) return <CrmLogin onLogin={handleLogin} />;

  const openTicketsCount = tickets.filter(t => t.status === "open").length;

  return (
    <Box dir="rtl" sx={{ display: "flex", minHeight: "100vh", background: C.bg, overflow: "hidden" }}>
      <Sidebar active={activeTab === "clientDetail" ? "clients" : activeTab} onChange={(tab) => { setActiveTab(tab); if (tab !== "clientDetail") setSelectedClientId(null); }} ticketsBadge={openTicketsCount} currentAgent={currentAgent} />

      {/* Main content */}
      <Box sx={{ flex: 1, ml: "240px", p: { xs: 2, md: 4 }, height: "100vh", overflowY: "auto" }}>
        {activeTab === "dashboard" && <Dashboard stats={stats} loading={loading.stats} onNavigate={setActiveTab} />}
        {activeTab === "clients" && <ClientsPage clients={clients} loading={loading.clients} onRefresh={fetchClients} snack={showSnack} onViewClient={handleViewClient} />}
        {activeTab === "clientDetail" && <ClientDetailPage clientId={selectedClientId} onBack={handleBackFromClient} snack={showSnack} onRefresh={fetchClients} />}
        {activeTab === "tickets" && <TicketsPage tickets={tickets} loading={loading.tickets} onRefresh={fetchTickets} snack={showSnack} agents={agents} clients={clients} />}
        {activeTab === "phones" && <PhonesPage phones={phones} loading={loading.phones} onRefresh={fetchPhones} snack={showSnack} />}
        {activeTab === "purchases" && <PurchasesPage loading={loading.clients} onRefresh={fetchClients} snack={showSnack} clients={clients} />}
        {activeTab === "packagesManage" && <PackagesManagementPage snack={showSnack} />}
        {activeTab === "agents" && <AgentsPage agents={agents} loading={loading.agents} onRefresh={fetchAgents} snack={showSnack} />}
        {activeTab === "broadcast" && <BroadcastPage snack={showSnack} />}
        {activeTab === "manage" && <ManagePage snack={showSnack} onSyncTickets={fetchTickets} />}
        {activeTab === "ai" && <AiDashboardPage snack={showSnack} />}
        {activeTab === "stats" && <StatsPage stats={stats} loading={loading.stats} />}
        {activeTab === "games" && <GamesPage games={games} loading={loading.games} onRefresh={fetchGames} />}
      </Box>

      <Snackbar open={snackState.open} autoHideDuration={3000} onClose={() => setSnackState(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackState.sev} variant="filled" sx={{ borderRadius: 3, fontWeight: 600 }}>{snackState.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CrmPanel;
